/**
 * `/admin/servers/:id` — per-VPS dashboard (OCP-78).
 *
 * Three panels:
 * 1. Top-bar: hostname / region / pool_tag / status + inline drain
 *    toggle + max_tenants edit.
 * 2. Top-cards: current load (RAM, CPU, slots, storage) + colored
 *    progress bars (green / yellow / red).
 * 3. Tenants-on-this-server table with per-tenant latest snapshot.
 * 4. 24h time-series chart (RAM-sum, CPU-avg, tenant_count).
 * 5. Lifecycle events panel (OCP-104) — chronological history of
 *    operator actions + system events from the ocp_audit_log.
 *
 * Data sources:
 *   - GET /v1/admin/vps-servers/:id              (server meta)
 *   - GET /v1/admin/vps-servers/:id/tenants      (tenants list)
 *   - GET /v1/admin/vps-servers/:id/telemetry-history?hours=24
 *   - GET /v1/admin/vps-servers/:id/lifecycle-events (OCP-104)
 *   - GET /v1/admin/telemetry/load-summary       (current capacity)
 */
import { useCallback, useEffect, useState } from 'react';

import { useTransientState } from '../lib/useTransientState';
import { SERVER_ROLES } from '../lib/pool-role';
import { Link, useParams } from 'react-router-dom';

import { LifecycleEventsPanel } from '../components/LifecycleEventsPanel';
import { AlertHistoryPanel } from '../components/AlertHistoryPanel';
import { PoolTagPicker } from '../components/PoolTagPicker';
import { SshHardeningPanel, type SshHardeningState } from '../components/SshHardeningPanel';
import { api, ApiError } from '../lib/api';
import { formatRelative } from './HomePage';
import { LoadHistoryChart } from '../components/LoadHistoryChart';
import {
  BINDING_LABEL,
  resolveEffectiveCapacity,
  type EffectiveCapacityInputs,
} from '../lib/effective-capacity';
import {
  isPoolRoleMismatch,
  isRoleLockedByPool,
  isTenantServingPool,
  presetRoleForPoolTag,
  type ServerRole,
} from '../lib/pool-role';
import { useDemoPoolTag } from '../lib/use-demo-pool-tag';
import { fmtDateTime, fmtDate } from '../lib/format-time';

interface ServerMeta {
  id: string;
  coolify_uuid: string | null;
  /** OCP-433: friendly display name (null -> falls back to hostname). */
  name: string | null;
  hostname: string;
  region: string;
  pool_tag: string;
  /** OCP-347/354: monitoring role — drives role-aware disk alerting. */
  server_role: ServerRole;
  hardware: string;
  ram_total_mb: number;
  cpu_cores: number;
  disk_gb: number;
  max_tenants: number;
  /** OCP-436: static uplink rating (Mbit/s) - network dimension of the model. */
  uplink_mbits?: number;
  /** OCP-436: operator HARD cap on the effective tenant count (null = none). */
  max_tenants_override?: number | null;
  /** OCP-436: dynamically-computed effective cap (null until first compute). */
  effective_max_tenants?: number | null;
  /** OCP-436: traceability blob for the current effective cap (null = never computed). */
  effective_capacity_inputs?: EffectiveCapacityInputs | null;
  /** OCP-436: ISO timestamp of the last effective-cap change. */
  effective_capacity_computed_at?: string | null;
  status: 'active' | 'draining' | 'decommissioned';
  /** OCP-101: per-server telemetry-source toggle. */
  metric_source: 'telemetry-push' | 'ssh-pull' | 'disabled';
  /** OCP-374: SSH endpoint for one-click agent install (key never returned). */
  ssh_host: string | null;
  ssh_port: number;
  ssh_user: string;
  ssh_configured: boolean;
  /** OCP-622: true for a host reached only through the host-agent beacon (no Coolify, no SSH). */
  agent_only?: boolean;
  /** OCP-386: which credential is stored. */
  ssh_auth_method?: 'key' | 'password' | 'none';
  /** OCP-755: key-only SSH - last passing hardening run, sshd's password login, the last run. */
  ssh_hardened_at?: string | null;
  ssh_password_auth?: 'disabled' | 'enabled' | null;
  ssh_hardening?: SshHardeningState['ssh_hardening'];
  /**
   * OCP-542: the pinned SSH host key. Null = nothing pinned yet, the next
   * connect trusts-on-first-use. A connect against a host presenting anything
   * else fails closed, and only a deliberate operator re-pin clears that.
   */
  ssh_host_key_fingerprint?: string | null;
  ssh_host_key_algo?: string | null;
  ssh_host_key_pinned_at?: string | null;
  /** OCP-930 - the endpoint was repointed: no credentialed connection until its host key is pinned. */
  ssh_endpoint_quarantined_at?: string | null;
  /** OCP-930 - first_use (trust on first use, not verified) | operator_confirmed | null. */
  ssh_host_key_pin_source?: 'first_use' | 'operator_confirmed' | null;
  /** OCP-469: last-seen host-agent build version (from the beacon), null until first beacon. */
  agent_version?: string | null;
  /** OCP-469: ISO timestamp when the current agent_version was first observed. */
  agent_version_seen_at?: string | null;
  /** OCP-469: the agent version baked into the running api image (convergence target). */
  agent_current_version?: string | null;
  /** OCP-469: true when the last-seen agent is an older SemVer than the baked version. */
  agent_outdated?: boolean;
  /** OCP-420: per-mount disk-badge display config (null = AUTO heuristic). */
  mount_config?: Record<string, 'primary' | 'include' | 'hidden'> | null;
  /** OCP-426: host OS-patch observability. */
  last_scanned_at?: string | null;
  last_patched_at?: string | null;
  packages_available?: number | null;
  security_packages_available?: number | null;
  /** OCP-592 - duration-gated patch-alert state (null = no state row yet). */
  patch_alert?: { security_pending_since: string | null; alerting: boolean } | null;
  reboot_pending?: boolean;
  /** OCP-741 - non-null = the last apt-get update failed; the counts are stale, not clean. */
  patch_scan_error?: string | null;
  unattended_timer?: 'active' | 'inactive' | 'missing' | string | null;
  unattended_last_run_at?: string | null;
  /** OCP-807 - OS release + running kernel (last scan / patch) and the drift against the active fleet. */
  os_release?: string | null;
  os_version_id?: string | null;
  kernel_version?: string | null;
  kernel_installed_version?: string | null;
  os_info_at?: string | null;
  os_drift?: { behind: boolean; reasons: string[]; fleet_os_version_id: string | null; fleet_kernel: string | null; kernel_major_minor: string | null; kernel_pending_reboot?: boolean } | null;
  /** OCP-93: drain flag - false = picker skips this host for new tenants. */
  accepts_new?: boolean;
  /** OCP-435: per-server host timezone override (null = follow region default). */
  timezone?: string | null;
  /** OCP-435: resolved effective timezone (override ?? region-map ?? UTC). */
  timezone_effective?: string;
  /** OCP-435: where the effective timezone came from. */
  timezone_source?: 'override' | 'region' | 'default';
  capacity?: {
    tenant_count: number;
    max_tenants: number;
    is_full: boolean;
    ram_used_mb_sum: number;
    ram_total_mb: number;
    cpu_pct_avg: number;
    cpu_cores: number;
    free_slots_pct: number;
  };
  created_at: string;
}

// OCP-347/354: monitoring role — distinct from pool_tag (placement). Storage
// and infra hosts alert at a stricter disk bar (85%) since RustFS/Postgres
// filling up is an outage; tenant hosts use the looser 90% overload bar.
const SERVER_ROLE_LABELS: Record<ServerMeta['server_role'], { label: string; desc: string }> = {
  tenant: {
    label: 'Tenant',
    desc: 'Runs customer tenants. Disk alerts at the standard overload bar (90%).',
  },
  storage: {
    label: 'Storage',
    desc: 'RustFS / object store. Disk alerts at the stricter bar (85%) — filling up = outage.',
  },
  infra: {
    label: 'Infra',
    desc: 'Postgres / internal services. Disk alerts at the stricter bar (85%).',
  },
  'ai-gateway': {
    label: 'AI gateway',
    desc: 'Managed-AI LiteLLM host (OCP-567). Never tenant-placeable; stricter disk bar (85%) plus the gateway capacity model (memory / cpu / throughput / upstream).',
  },
  'control-plane': {
    label: 'Control plane',
    desc: 'The host that runs OCP itself (OCP-807). Never tenant-placeable (pool control-plane); the nightly run patches it last and reboots it only inside the maintenance window, by a delayed shutdown after the run is reported. Stricter disk bar (85%).',
  },
  'ai-backend': {
    label: 'AI backend',
    desc: 'Managed-AI model host - the vLLM embedding backend (OCP-622). Never tenant-placeable; stricter disk bar (85%), per-GPU VRAM / utilisation alerts and the backend dimension of the gateway capacity model.',
  },
};

const METRIC_SOURCE_LABELS: Record<ServerMeta['metric_source'], { label: string; desc: string }> = {
  'telemetry-push': {
    label: 'Telemetry-push (default)',
    desc: 'Poller talks to Coolify on the normal 5-min schedule.',
  },
  'ssh-pull': {
    label: 'SSH-pull (Coolify unreliable)',
    desc: 'Skip Coolify; use the SSH-pull fallback. Not yet wired - currently no-ops with a log warning per host.',
  },
  disabled: {
    label: 'Disabled (kill-switch)',
    desc: 'Skip telemetry entirely for this server. Tenants keep running; only metrics are paused.',
  },
};

interface TenantOnServer {
  id: string;
  subdomain: string;
  status: string;
  expected_team_size: string | null;
  capacity_weight: number;
  last_snapshot_at: string | null;
  ram_used_mb_avg: number | null;
  cpu_pct_avg: number | null;
  storage_total_gb: number | null;
}

interface HistoryPoint {
  bucket: string;
  tenant_count: number;
  ram_used_mb_sum: number;
  cpu_pct_avg: number;
}

export function AdminServerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [server, setServer] = useState<ServerMeta | null>(null);
  const [tenants, setTenants] = useState<TenantOnServer[]>([]);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [historyMode, setHistoryMode] = useState<'chart' | 'table'>('chart');
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useTransientState<string | null>(null, 4000);
  const [regionDraft, setRegionDraft] = useState<string | null>(null);
  // OCP-433: inline editors for the friendly name + the real hostname. `null`
  // = not editing; a string = the current draft value being edited.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [hostnameDraft, setHostnameDraft] = useState<string | null>(null);
  // OCP-432: live `tenant.demo_pool_tag` so the pool picker's demo option +
  // the pool/role consistency checks below track the setting.
  const demoPoolTag = useDemoPoolTag();

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [tenantsRes, historyRes, loadRes] = await Promise.all([
        api<{ server: ServerMeta; items: TenantOnServer[] }>(
          `/v1/admin/vps-servers/${id}/tenants`,
        ),
        api<{ items: HistoryPoint[] }>(
          `/v1/admin/vps-servers/${id}/telemetry-history?hours=24`,
        ),
        api<{ items: Array<{ vps_server_id: string; tenant_count: number; weighted_slots: number; ram_used_mb_sum: number; cpu_pct_avg: number; storage_total_gb_sum: number; tenants_with_telemetry: number; free_slots_pct: number }> }>(
          `/v1/admin/telemetry/load-summary`,
        ),
      ]);
      const capacityRow = loadRes.items.find((r) => r.vps_server_id === id);
      // OCP-551: is_full must gate on the EFFECTIVE cap, not the static
      // baseline - the whole point of the OCP-436 model is that a host can
      // be full (or have headroom) at a count the static column disagrees
      // with. resolveEffectiveCapacity() is the same fallback ladder the
      // capacity cards below use.
      const effectiveCap = resolveEffectiveCapacity(tenantsRes.server).effective;
      setServer({
        ...tenantsRes.server,
        capacity: capacityRow
          ? {
              tenant_count: capacityRow.weighted_slots,
              max_tenants: effectiveCap,
              is_full: capacityRow.weighted_slots >= effectiveCap,
              ram_used_mb_sum: capacityRow.ram_used_mb_sum,
              ram_total_mb: tenantsRes.server.ram_total_mb,
              cpu_pct_avg: capacityRow.cpu_pct_avg,
              cpu_cores: tenantsRes.server.cpu_cores,
              free_slots_pct: capacityRow.free_slots_pct,
            }
          : undefined,
      });
      setTenants(tenantsRes.items);
      setHistory(historyRes.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Server not found.');
      } else if (err instanceof ApiError) {
        setError(`Could not load server (HTTP ${err.status}).`);
      } else {
        setError('Could not load server.');
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // OCP-538 - lifecycle-bound flash (this file's bare setTimeout was the
  // CI failure in run 2293: the timer fired after test teardown and the
  // late setState crashed react-dom under runner load).
  const flash = useCallback(
    (msg: string) => setActionMsg(msg),
    [setActionMsg],
  );

  const handleStatusToggle = useCallback(async () => {
    if (!server) return;
    const next = server.status === 'active' ? 'draining' : 'active';
    try {
      await api(`/v1/admin/vps-servers/${server.id}`, {
        method: 'PATCH',
        body: { status: next },
      });
      flash(`${server.hostname} → ${next}`);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
      setError(`Could not change status (${msg}).`);
    }
  }, [server, load, flash]);

  const handleMaxTenantsSave = useCallback(
    async (value: number) => {
      if (!server) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { max_tenants: value },
        });
        flash(`max_tenants → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update max_tenants (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-436: static uplink rating (Mbit/s) - the network dimension of the
  // capacity model. Generic PATCH, audit-logged as vps_server.update.
  const handleUplinkSave = useCallback(
    async (value: number) => {
      if (!server) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { uplink_mbits: value },
        });
        flash(`uplink_mbits → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update uplink_mbits (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-436: operator HARD cap on the effective tenant count. null clears it
  // (the dynamic model may then raise the cap above static within the ceiling).
  const handleOverrideSave = useCallback(
    async (value: number | null) => {
      if (!server) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { max_tenants_override: value },
        });
        flash(value == null ? 'override cleared' : `max_tenants_override → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update max_tenants_override (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-101: dedicated metric-source toggle. Posts to the single-
  // purpose endpoint so the audit-log captures `vps_server.metric_
  // source.changed` instead of a generic PATCH.
  const handleMetricSourceChange = useCallback(
    async (value: ServerMeta['metric_source']) => {
      if (!server || server.metric_source === value) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}/metric-source`, {
          method: 'PATCH',
          body: { metric_source: value },
        });
        flash(`metric_source → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not change metric_source (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-347/354: server-role change. Posts the generic PATCH (the role lives
  // alongside other metadata) — audit-logged as `vps_server.update`.
  const handleServerRoleChange = useCallback(
    async (value: ServerMeta['server_role']) => {
      if (!server || server.server_role === value) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { server_role: value },
        });
        flash(`server_role → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not change server_role (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-433: friendly display name editor. Empty string clears it back to the
  // hostname fallback (the API normalizes blank -> null). Audited vps_server.update.
  const handleNameSave = useCallback(
    async (value: string) => {
      const next = value.trim();
      if (!server || next === (server.name ?? '')) {
        setNameDraft(null);
        return;
      }
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, { method: 'PATCH', body: { name: next } });
        flash(next ? `name → ${next}` : 'name cleared (falls back to hostname)');
        setNameDraft(null);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update name (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-433: real-hostname editor. Distinct from the display name - this is the
  // box's actual host. Non-empty (the column is notNull). Audited vps_server.update.
  const handleHostnameSave = useCallback(
    async (value: string) => {
      const next = value.trim();
      if (!server || !next || server.hostname === next) {
        setHostnameDraft(null);
        return;
      }
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, { method: 'PATCH', body: { hostname: next } });
        flash(`hostname → ${next}`);
        setHostnameDraft(null);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update hostname (${msg}).`);
      }
    },
    [server, load, flash],
  );

  // OCP-388: region isn't telemetry-derivable (specs/arch self-fill from
  // the host-agent, but geo isn't) — operator sets it here.
  const handleRegionSave = useCallback(
    async (value: string) => {
      const next = value.trim();
      if (!server || !next || server.region === next) {
        setRegionDraft(null);
        return;
      }
      try {
        const resp = await api<{ region_warning?: string | null }>(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { region: next },
        });
        // OCP-445: warn (non-blocking) when the new region has no timezone mapping.
        flash(resp?.region_warning ? `region -> ${next} - warning: ${resp.region_warning}` : `region → ${next}`);
        setRegionDraft(null);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update region (${msg}).`);
      }
    },
    [server, load, flash],
  );

  if (error) {
    return (
      <div className="p-6">
        <Link to="/servers" className="text-sm text-brand-600 hover:underline">
          ← back to servers
        </Link>
        <div className="mt-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!server) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  // OCP-551: resolve once per render - the top capacity card and the
  // EffectiveCapacityPanel below must agree on the same number.
  const tenantSlotCap = resolveEffectiveCapacity(server);

  return (
    <div className="space-y-6 p-6">
      <div>
        <Link to="/servers" className="text-sm text-brand-600 hover:underline">
          ← back to servers
        </Link>
        {/* OCP-433: editable friendly display name (falls back to hostname
            when blank). Click to edit; Enter saves, Escape cancels. */}
        <h1 className="mt-2 text-xl font-semibold">
          {nameDraft === null ? (
            <button
              type="button"
              onClick={() => setNameDraft(server.name ?? '')}
              className="rounded underline-offset-4 hover:underline decoration-dotted"
              title="Edit display name (blank = use hostname)"
            >
              {server.name?.trim() || server.hostname}
            </button>
          ) : (
            <input
              autoFocus
              defaultValue={nameDraft}
              onBlur={(e) => void handleNameSave(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleNameSave((e.target as HTMLInputElement).value);
                if (e.key === 'Escape') setNameDraft(null);
              }}
              placeholder="display name (blank = use hostname)"
              className="w-80 max-w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-lg"
            />
          )}
        </h1>
        <p className="mt-1 text-xs text-gray-500">
          {/* OCP-433: the box's REAL hostname, editable inline. */}
          <span className="text-gray-400">host:</span>{' '}
          {hostnameDraft === null ? (
            <button
              type="button"
              onClick={() => setHostnameDraft(server.hostname)}
              className="font-mono underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
              title="Edit real hostname"
            >
              {server.hostname}
            </button>
          ) : (
            <input
              autoFocus
              defaultValue={hostnameDraft}
              onBlur={(e) => void handleHostnameSave(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleHostnameSave((e.target as HTMLInputElement).value);
                if (e.key === 'Escape') setHostnameDraft(null);
              }}
              className="w-56 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-1 py-0.5 font-mono text-xs"
            />
          )}{' '}
          ·{' '}
          {regionDraft === null ? (
            <button
              type="button"
              onClick={() => setRegionDraft(server.region)}
              className="underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
              title="Edit region"
            >
              {server.region}
            </button>
          ) : (
            <input
              autoFocus
              defaultValue={regionDraft}
              onBlur={(e) => void handleRegionSave(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRegionSave((e.target as HTMLInputElement).value);
                if (e.key === 'Escape') setRegionDraft(null);
              }}
              className="w-28 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-1 py-0.5 text-xs"
            />
          )}{' '}
          · {server.hardware} · {server.pool_tag} ·{' '}
          <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 font-medium text-gray-700 dark:text-gray-300">
            {SERVER_ROLE_LABELS[server.server_role].label}
          </span>{' '}
          ·{' '}
          {server.agent_only ? (
            <span className="rounded bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5 font-medium text-sky-800 dark:text-sky-200" title="No Coolify server, no SSH endpoint - the host-agent beacon is the only channel (OCP-622).">
              agent-only (no Coolify, no SSH)
            </span>
          ) : (
            <span className="font-mono">{server.coolify_uuid}</span>
          )}
        </p>
      </div>

      {actionMsg && (
        <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
          {actionMsg}
        </div>
      )}

      {/* Top action bar */}
      <div className="flex items-center gap-3 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <StatusBadge status={server.status} />
        <button
          type="button"
          onClick={handleStatusToggle}
          disabled={server.status === 'decommissioned'}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
        >
          {server.status === 'active' ? 'Drain' : 'Resume'}
        </button>
        <MaxTenantsEditor value={server.max_tenants} onSave={handleMaxTenantsSave} />
      </div>

      {/* OCP-432: placement-pool (pool_tag) editor. Was read-only until now,
          so a mis-pooled / re-pool-needing server had to be re-registered.
          Picker-driven (OCP-428) with the OCP-365 role preset/lock carried
          into the same PATCH, plus a guard against orphaning tenant
          placement. Rendered above the monitoring-role fieldset since
          placement precedes monitoring. */}
      <PoolTagEditor
        server={server}
        tenantCount={tenants.length}
        demoPoolTag={demoPoolTag}
        onSaved={load}
        flash={flash}
        setError={setError}
      />

      {/* OCP-347/354: server-role toggle. Distinct from pool_tag — this is
          the monitoring axis that decides the disk-alert strictness. Lets the
          operator mark the RustFS/storage host without curl. Disabled when
          decommissioned. */}
      <fieldset
        data-testid="server-role-toggle"
        className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
        disabled={server.status === 'decommissioned'}
      >
        <legend className="px-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Server role (monitoring)
        </legend>
        <div className="mt-2 space-y-2 text-sm">
          {SERVER_ROLES.map((value) => {
            const meta = SERVER_ROLE_LABELS[value];
            // OCP-365: a role that clashes with this server's pool_tag is
            // disabled (the API would reject it with pool_role_mismatch).
            // Never disable the currently-selected role so the radio group
            // always shows a valid selection.
            const clashes =
              server.server_role !== value &&
              isPoolRoleMismatch(server.pool_tag, value as ServerRole, demoPoolTag);
            return (
              <label
                key={value}
                className={`flex items-start gap-3 ${clashes ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <input
                  type="radio"
                  name="server_role"
                  value={value}
                  checked={server.server_role === value}
                  disabled={clashes}
                  onChange={() => void handleServerRoleChange(value)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{meta.label}</span>
                  <span className="ml-2 text-gray-500">{meta.desc}</span>
                  {clashes && (
                    <span className="ml-2 text-amber-600 dark:text-amber-400">
                      - not allowed for pool_tag <code>{server.pool_tag}</code>
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* OCP-101: metric-source toggle. 3-way radio so the operator can
          flip a known-broken server to 'disabled' (kill telemetry-spam)
          or to 'ssh-pull' (pre-flag for OCP-103). Disabled when the
          server itself is decommissioned. */}
      <fieldset
        data-testid="metric-source-toggle"
        className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
        disabled={server.status === 'decommissioned'}
      >
        <legend className="px-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Telemetry source
        </legend>
        <div className="mt-2 space-y-2 text-sm">
          {(['telemetry-push', 'ssh-pull', 'disabled'] as const).map((value) => {
            const meta = METRIC_SOURCE_LABELS[value];
            return (
              <label key={value} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="metric_source"
                  value={value}
                  checked={server.metric_source === value}
                  onChange={() => void handleMetricSourceChange(value)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{meta.label}</span>
                  <span className="ml-2 text-gray-500">{meta.desc}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* OCP-435: host OS timezone. Region default applied at register; operator
          overrides here (re-applies the OS clock over SSH). UTC stays a valid
          explicit choice for fleet-wide consistency. */}
      <TimezoneEditor server={server} onSaved={load} flash={flash} setError={setError} />

      {/* Capacity cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* OCP-551: the denominator is the EFFECTIVE cap - the number the
            scheduler actually gates placement on - not the static
            `max_tenants` hardware-class default. The two are allowed to
            disagree in either direction (demo host capped down to 1;
            STRATO hosts raised above their static baseline by dwell
            headroom), so this must never collapse to Math.min(static,
            effective). See EffectiveCapacityPanel below for the full
            static-vs-effective breakdown + editors. */}
        <CapacityCard
          title="Tenant slots"
          used={server.capacity?.tenant_count ?? 0}
          total={tenantSlotCap.effective}
          unit="slots"
          caption={
            tenantSlotCap.notYetComputed
              ? `static ${tenantSlotCap.static} - effective cap not yet evaluated`
              : tenantSlotCap.effective !== tenantSlotCap.static
                ? `static ${tenantSlotCap.static} - ${tenantSlotCap.binding ? BINDING_LABEL[tenantSlotCap.binding] : 'model-adjusted'}${tenantSlotCap.reason ? `: ${tenantSlotCap.reason}` : ''}`
                : undefined
          }
        />
        <CapacityCard
          title="RAM"
          used={Math.round((server.capacity?.ram_used_mb_sum ?? 0) / 1024)}
          total={Math.round(server.ram_total_mb / 1024)}
          unit="GB"
        />
        <CapacityCard
          title="CPU"
          used={Math.round(server.capacity?.cpu_pct_avg ?? 0)}
          total={100}
          unit="%"
        />
      </div>

      {/* OCP-436: dynamic effective-capacity snapshot - static vs effective +
          the exact inputs that produced the current value + uplink/override
          editors. */}
      <EffectiveCapacityPanel
        server={server}
        onSaveUplink={handleUplinkSave}
        onSaveOverride={handleOverrideSave}
      />

      {/* OCP-464 - live NIC throughput + FairFlat month-to-date network volume
          (with the per-server monthly-allowance override editor). */}
      {id && <NetworkPanel serverId={id} flash={flash} setError={setError} />}

      {/* OCP-622 - per-GPU VRAM / utilisation / temperature from the beacon and,
          on the model host, vLLM's own queue / KV-cache numbers. */}
      {id && <GpuPanel serverId={id} serverRole={server.server_role} agentOnly={Boolean(server.agent_only)} />}

      {/* OCP-420 - disk-badge mount display config. Prefilled from the AUTO
          heuristic (system/tiny mounts hidden); operator confirms/overrides. */}
      {id && <MountDisplayPanel serverId={id} flash={flash} />}

      {/* Tenants table */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
          Tenants on this server ({tenants.length})
        </h2>
        {tenants.length === 0 ? (
          <p className="text-sm text-gray-500">No tenants assigned to this server yet.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2">Subdomain</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Team-size</th>
                  <th className="px-3 py-2 text-right">Weight</th>
                  <th className="px-3 py-2 text-right">RAM MB</th>
                  <th className="px-3 py-2 text-right">CPU %</th>
                  <th className="px-3 py-2 text-right">Storage GB</th>
                  <th className="px-3 py-2 text-right">Last snapshot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {tenants.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-3 py-2">
                      <Link to={`/tenants/${t.id}`} className="text-brand-600 hover:underline">
                        {t.subdomain}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{t.status}</td>
                    <td className="px-3 py-2">
                      {t.expected_team_size ? (
                        <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-800">
                          {t.expected_team_size}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.capacity_weight}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.ram_used_mb_avg ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.cpu_pct_avg !== null ? `${Math.round(t.cpu_pct_avg)}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.storage_total_gb !== null ? t.storage_total_gb.toFixed(1) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">
                      {t.last_snapshot_at
                        ? fmtDateTime(t.last_snapshot_at)
                        : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {server.agent_only ? (
        /* OCP-622 - no SSH channel: the SSH-driven panels (credential, agent
           install, bootstrap, patching, reboot, disk report) do not apply. */
        <section className="rounded border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-4 text-sm text-sky-900 dark:text-sky-200" data-testid="agent-only-notice">
          <h2 className="text-sm font-semibold">Agent-only host - no SSH from orboto Control</h2>
          <p className="mt-1 text-xs">
            This box is not in orboto Control's Coolify and orboto Control cannot SSH into it (LAN-only,
            its own Coolify). The host-agent beacon is the only channel: telemetry, GPU probe, overload
            alerts and the capacity model work; agent install, host bootstrap, OS patching, reboot,
            disk reports and host cleanup do not (the API answers <code>409 ssh_not_applicable</code>). Reinstall or
            upgrade the agent on the box itself with a fresh secret from{' '}
            <code>POST /v1/admin/vps-servers/{server.id}/agent-secret</code>.
          </p>
        </section>
      ) : (
        <>
          {/* SSH access + one-click agent install (OCP-374). */}
          <ServerSshPanel server={server} onChanged={load} flash={flash} />

          {/* OCP-438: re-run the host bootstrap (Docker install + hostname detect +
              OS timezone + Coolify validate) on an already-registered box. Brings a
              pre-registered / drifted server to Coolify-usable without manual SSH. */}
          <HostBootstrapPanel server={server} onChanged={load} flash={flash} />

          {/* OCP-426: host OS patching - scan / full-patch (no reboot) / controlled
              reboot (confirm + maintenance-window gated for a tenant host). */}
          <HostPatchingPanel server={server} onChanged={load} flash={flash} />
          {/* OCP-605: what is on this disk + abnormal-growth state. */}
          <HostDiskPanel server={server} flash={flash} />
          {/* OCP-639: host cleanup - plan (dry run) -> confirm -> run, last runs. */}
          <HostCleanupPanel server={server} flash={flash} />
        </>
      )}

      {/* Lifecycle events (OCP-104) — operator actions + system
          events for this server. Newest-first, filterable, paginated. */}
      {id && <AlertHistoryPanel serverId={id} />}

      {id && <LifecycleEventsPanel serverId={id} />}

      {/* 24h history — interactive Recharts time-series (OCP-105) with
          a "View as table" toggle that falls back to the SparklineTable
          we shipped as the OCP-78 interim. Both modes useful: chart
          for trends, table for exact numbers + the older muscle memory. */}
      <section>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Load history ({history.length} buckets)
          </h2>
          <button
            type="button"
            onClick={() => setHistoryMode((m) => (m === 'chart' ? 'table' : 'chart'))}
            className="text-xs text-brand-500 hover:underline"
          >
            View as {historyMode === 'chart' ? 'table' : 'chart'}
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">No telemetry recorded in the last 24 hours.</p>
        ) : historyMode === 'chart' ? (
          <LoadHistoryChart rows={history} />
        ) : (
          <SparklineTable rows={history} />
        )}
      </section>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------

/**
 * OCP-432 - placement-pool (pool_tag) editor. Before this the detail page
 * rendered pool_tag read-only, so a mis-pooled server (or one that needed
 * re-pooling, e.g. tenant-pool -> demo) had to be re-registered. Uses the
 * shared PoolTagPicker (OCP-428, demo + known pools + custom escape hatch)
 * and carries the OCP-365 role preset/lock into the SAME PATCH, so a pool
 * change never leaves an inconsistent pool_tag/server_role pair (the API
 * rejects that with 400 pool_role_mismatch, surfaced here). Saving goes
 * through the generic admin PATCH (audited `vps_server.update`).
 *
 * Guard: moving a server that still HOSTS tenants out of a tenant-serving
 * pool would exclude it from the tenant pool while its tenants keep running
 * on the box (their placement is unchanged) - a silent orphaning risk. We
 * reuse the drain-style messaging and require an explicit confirm before
 * saving that transition, rather than blocking it server-side (a legitimate
 * mid-migration re-pool must stay possible; MCP/direct-API callers already
 * bypass UI confirms by design, same as decommission).
 */
function PoolTagEditor({
  server,
  tenantCount,
  demoPoolTag,
  onSaved,
  flash,
  setError,
}: {
  server: ServerMeta;
  tenantCount: number;
  demoPoolTag: string;
  onSaved: () => Promise<void> | void;
  flash: (msg: string) => void;
  setError: (msg: string | null) => void;
}) {
  const [draft, setDraft] = useState(server.pool_tag);
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the server reloads with a new pool_tag (after a
  // save, or an external change), so the picker reflects persisted state.
  useEffect(() => {
    setDraft(server.pool_tag);
  }, [server.pool_tag]);

  const trimmed = draft.trim();
  const dirty = trimmed !== '' && trimmed !== server.pool_tag;
  const presetRole = presetRoleForPoolTag(trimmed, demoPoolTag);
  const roleLocked = isRoleLockedByPool(trimmed, demoPoolTag);
  // The role we'll persist: the pool's preset when it constrains one, else
  // keep the server's current role (unconstrained pools allow any role).
  const roleToSend = presetRole ?? server.server_role;
  const roleWillChange = roleToSend !== server.server_role;

  const save = useCallback(async () => {
    if (saving || !dirty) return;
    // OCP-432 guard - reuse drain semantics: a server that still hosts
    // tenants leaving a tenant-serving pool would be excluded from the
    // tenant placement pool while its tenants keep running on the box.
    // Warn + require explicit confirmation rather than silently orphaning.
    const leavingTenantPool =
      tenantCount > 0 &&
      isTenantServingPool(server.pool_tag, demoPoolTag) &&
      !isTenantServingPool(trimmed, demoPoolTag);
    if (leavingTenantPool) {
      const ok = window.confirm(
        `${server.hostname} currently hosts ${tenantCount} tenant${tenantCount === 1 ? '' : 's'}.\n\n` +
          `Moving it from pool '${server.pool_tag}' to '${trimmed}' removes it from the tenant ` +
          `placement pool. The running tenants stay on this box (their placement is unchanged) but ` +
          `the server is monitored as '${roleToSend}' and excluded from new tenant placement.\n\n` +
          `To move the tenants off first, drain the server and let them migrate. Continue anyway?`,
      );
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      await api(`/v1/admin/vps-servers/${server.id}`, {
        method: 'PATCH',
        body: {
          pool_tag: trimmed,
          // Send server_role only when the pool constrains a role that
          // differs from the current one, so the resulting pair stays
          // consistent (OCP-365). Unconstrained pools keep the role as-is.
          ...(presetRole != null && roleWillChange ? { server_role: presetRole } : {}),
        },
      });
      flash(
        roleWillChange ? `pool_tag → ${trimmed} · server_role → ${roleToSend}` : `pool_tag → ${trimmed}`,
      );
      await onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = (err.body ?? {}) as { error?: string; message?: string };
        if (body.error === 'pool_role_mismatch') {
          setError(body.message ?? 'Pool tag and server role are inconsistent.');
        } else {
          setError(`Could not update pool_tag (HTTP ${err.status}).`);
        }
      } else {
        setError('Could not update pool_tag.');
      }
    } finally {
      setSaving(false);
    }
  }, [
    saving,
    dirty,
    tenantCount,
    server,
    demoPoolTag,
    trimmed,
    presetRole,
    roleWillChange,
    roleToSend,
    flash,
    onSaved,
    setError,
  ]);

  return (
    <fieldset
      data-testid="pool-tag-editor"
      className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
      disabled={server.status === 'decommissioned'}
    >
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-gray-300">
        Placement pool
      </legend>
      <div className="mt-2 max-w-md space-y-2 text-sm">
        <PoolTagPicker
          value={draft}
          onChange={setDraft}
          demoPoolTag={demoPoolTag}
          selectTestId="server-pool-tag"
          customTestId="server-pool-tag-custom"
        />
        <p className="text-xs text-gray-500">
          Placement pool the tenant picker routes to - separate from the monitoring role below.{' '}
          {presetRole != null ? (
            roleLocked ? (
              <>
                Server role will be set to <code>{presetRole}</code> (locked: <code>{trimmed}</code> is
                a tenant-serving pool).
              </>
            ) : (
              <>
                Server role will be preset to <code>{presetRole}</code> for <code>{trimmed}</code>.
              </>
            )
          ) : (
            <>Unconstrained pool - the current server role is kept.</>
          )}
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save pool'}
          </button>
          {dirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400" data-testid="pool-tag-dirty">
              {server.pool_tag} → {trimmed}
            </span>
          )}
        </div>
      </div>
    </fieldset>
  );
}

/**
 * OCP-435 - host OS timezone editor. Shows the effective timezone (override,
 * else the region default, else UTC) + its source, and lets the operator set a
 * per-server override or reset to the region default. Saving PATCHes the
 * dedicated `/timezone` endpoint, which persists the override AND re-applies
 * the OS clock over SSH (`timedatectl set-timezone`) - the fix path for an
 * already-registered box that is stuck on UTC.
 *
 * Note: this sets the box's OS clock only. Coolify's own `server_timezone`
 * display setting has no public API to write (Coolify v4), so it is set in the
 * Coolify UI if the operator wants Coolify's scheduled-task display aligned.
 */
interface TimezoneSaveResult {
  effective_timezone: string;
  timezone_source: 'override' | 'region' | 'default';
  os_applied: boolean;
  os_status: 'set' | 'failed' | 'unsupported' | 'skipped' | 'ssh_not_configured';
  os_detail: string | null;
}

const TZ_SOURCE_LABELS: Record<NonNullable<ServerMeta['timezone_source']>, string> = {
  override: 'operator override',
  region: 'region default',
  default: 'fleet default (UTC)',
};

function TimezoneEditor({
  server,
  onSaved,
  flash,
  setError,
}: {
  server: ServerMeta;
  onSaved: () => Promise<void> | void;
  flash: (msg: string) => void;
  setError: (msg: string | null) => void;
}) {
  const [draft, setDraft] = useState(server.timezone ?? '');
  const [saving, setSaving] = useState(false);
  const [osResult, setOsResult] = useState<TimezoneSaveResult | null>(null);

  // Re-sync when the server reloads with a new override (after save).
  useEffect(() => {
    setDraft(server.timezone ?? '');
  }, [server.timezone]);

  const effective = server.timezone_effective ?? server.timezone ?? 'UTC';
  const source = server.timezone_source ?? (server.timezone ? 'override' : 'default');
  const trimmed = draft.trim();
  const dirty = trimmed !== (server.timezone ?? '');

  const save = useCallback(
    async (value: string) => {
      if (saving) return;
      setSaving(true);
      setError(null);
      setOsResult(null);
      try {
        const res = await api<TimezoneSaveResult>(`/v1/admin/vps-servers/${server.id}/timezone`, {
          method: 'PATCH',
          body: { timezone: value.trim() },
        });
        setOsResult(res);
        flash(
          value.trim()
            ? `timezone → ${res.effective_timezone}`
            : `timezone reset to region default (${res.effective_timezone})`,
        );
        await onSaved();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
        setError(`Could not update timezone (${msg}).`);
      } finally {
        setSaving(false);
      }
    },
    [saving, server.id, flash, onSaved, setError],
  );

  return (
    <fieldset
      data-testid="timezone-editor"
      className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
      disabled={server.status === 'decommissioned'}
    >
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-gray-300">
        Host timezone (OS)
      </legend>
      <div className="mt-2 max-w-md space-y-2 text-sm">
        <p className="text-xs text-gray-500">
          Effective:{' '}
          <span className="font-mono font-medium text-gray-800 dark:text-gray-200" data-testid="tz-effective">
            {effective}
          </span>{' '}
          <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-400">
            {TZ_SOURCE_LABELS[source]}
          </span>
        </p>
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Override (IANA zone, e.g. <code>Europe/Berlin</code> or <code>UTC</code>; blank = region default)
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="blank = follow region default"
            data-testid="tz-input"
            className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-mono text-sm"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void save(draft)}
            disabled={!dirty || saving}
            data-testid="tz-save"
            className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save timezone'}
          </button>
          {server.timezone && (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                void save('');
              }}
              disabled={saving}
              data-testid="tz-reset"
              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              Reset to region default
            </button>
          )}
          {dirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {(server.timezone ?? '(region default)')} → {trimmed || '(region default)'}
            </span>
          )}
        </div>
        {osResult && (
          <p className="text-xs" data-testid="tz-os-result">
            {osResult.os_status === 'set' ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                OS clock set to {osResult.effective_timezone} over SSH.
              </span>
            ) : osResult.os_status === 'ssh_not_configured' ? (
              <span className="text-amber-600 dark:text-amber-400">
                Override saved. No SSH credential stored, so the OS clock was not changed - add one in SSH &amp; host-agent below, then re-save.
              </span>
            ) : osResult.os_status === 'unsupported' ? (
              <span className="text-amber-600 dark:text-amber-400">
                Override saved. The box has no <code>timedatectl</code> (non-systemd host) - set the OS timezone manually.
              </span>
            ) : (
              <span className="text-red-700 dark:text-red-400">
                Override saved, but applying the OS clock over SSH did not succeed (status: {osResult.os_status}
                {osResult.os_detail ? `: ${osResult.os_detail}` : ''}).
              </span>
            )}
          </p>
        )}
        <p className="text-[11px] text-gray-400">
          Sets the box OS clock only. Coolify&apos;s own server_timezone display setting has no public API - set it in
          the Coolify server UI if you want Coolify&apos;s scheduled-task display aligned.
        </p>
      </div>
    </fieldset>
  );
}

/**
 * OCP-374 — SSH access + one-click host-agent install. Stores the SSH
 * endpoint + key (encrypted server-side; never returned here), then runs
 * install.sh over SSH. The manual copy-paste one-liner stays available
 * via the agent-secret route as a fallback for boxes without a key.
 */
interface InstallResult {
  ok: boolean;
  exit_code?: number | null;
  stdout?: string;
  stderr?: string;
  reason?: string;
  message?: string;
  error?: string;
}

interface TestResult {
  ok: boolean;
  connected: boolean;
  user?: string | null;
  sudo?: 'passwordless' | 'needs-password' | 'n/a' | 'unknown';
  reason?: string;
  message?: string;
  /** OCP-542: set when `reason === 'host_key_mismatch'`. */
  host_key?: { expected_fingerprint: string; observed_fingerprint: string } | null;
}

/** OCP-542 - one host key as the API serialises it (fingerprint, never the blob). */
interface HostKeyWire {
  fingerprint: string;
  algo: string;
  pinned_at: string | null;
}

/** OCP-542 - result of the credential-free host-key scan. */
interface HostKeyScanResult {
  ok: boolean;
  reason?: string;
  message?: string;
  pinned: HostKeyWire | null;
  observed: HostKeyWire | null;
  /** null = nothing pinned yet (so this would be a first pin, not a re-pin). */
  matches: boolean | null;
}

/** OCP-930 - POST .../ssh/preview: the new endpoint's host key + a single-use confirmation. */
interface SshChangePreview {
  proposed: { ssh_host: string; ssh_port: number; ssh_user: string; auth: 'key' | 'password' };
  endpoint_changed: boolean;
  observed_host_key: HostKeyWire;
  matches_pin: boolean | null;
  quarantine_after: boolean;
  effect: string;
  confirmation_token: string;
}

interface RotateResult {
  ok: boolean;
  rotated?: boolean;
  coolify_updated?: boolean | null;
  old_key_removed?: boolean;
  stage?: string;
  reason?: string;
  message?: string;
}

function ServerSshPanel({
  server,
  onChanged,
  flash,
}: {
  server: ServerMeta;
  onChanged: () => Promise<void> | void;
  flash: (msg: string) => void;
}) {
  const [showForm, setShowForm] = useState(!server.ssh_configured);
  const [host, setHost] = useState(server.ssh_host ?? '');
  const [port, setPort] = useState(String(server.ssh_port ?? 22));
  const [user, setUser] = useState(server.ssh_user ?? 'root');
  const [authMethod, setAuthMethod] = useState<'key' | 'password'>('key');
  const [privateKey, setPrivateKey] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<InstallResult | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showRotate, setShowRotate] = useState(false);
  const [coolifyKeyUuid, setCoolifyKeyUuid] = useState('');
  const [rotating, setRotating] = useState(false);
  const [rotateResult, setRotateResult] = useState<RotateResult | null>(null);
  // OCP-542 - host-key pin workflow: scan (credential-free) then a deliberate
  // confirm. `scan` is what the operator inspects before re-pinning.
  const [scanning, setScanning] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [scan, setScan] = useState<HostKeyScanResult | null>(null);
  // OCP-930 - why the operator trusts the key they pin (audited).
  const [pinReason, setPinReason] = useState('');
  // OCP-930 - an SSH change is previewed first: the NEW endpoint's host key
  // (scanned without a credential) + a single-use confirmation.
  const [sshPreview, setSshPreview] = useState<SshChangePreview | null>(null);

  function sshBody() {
    return {
      ssh_host: host.trim(),
      ssh_port: Number(port) || 22,
      ssh_user: user.trim() || 'root',
      ...(authMethod === 'key' ? { ssh_private_key: privateKey } : { ssh_password: password }),
    };
  }

  function sshEdited<T>(set: (v: T) => void): (v: T) => void {
    return (v: T) => {
      set(v);
      setSshPreview(null);
    };
  }

  function sshError(e: unknown, what: string): string {
    const code = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : undefined;
    if (code === 'needs_a_person' || code === 'forbidden_for_role') return `${what}: only a super_admin person may change SSH access.`;
    if (code === 'second_factor_required' || code === 'mfa_enrollment_required') return `${what}: sign in with your second factor first.`;
    if (code === 'host_key_scan_failed') return `${what}: the new endpoint did not answer the host-key scan.`;
    if (code === 'confirmation_mismatch' || code === 'confirmation_used' || code === 'confirmation_expired' || code === 'ssh_config_changed') return `${what}: the preview is no longer valid - preview again.`;
    return e instanceof ApiError ? `${what} (HTTP ${e.status}).` : `${what}.`;
  }

  async function saveSsh() {
    if (saving) return;
    const secret = authMethod === 'key' ? privateKey : password;
    if (!host.trim() || !secret.trim()) {
      setErr(`Host and ${authMethod === 'key' ? 'private key' : 'password'} are required.`);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      setSshPreview(await api<SshChangePreview>(`/v1/admin/vps-servers/${server.id}/ssh/preview`, { method: 'POST', body: sshBody() }));
    } catch (e) {
      setErr(sshError(e, 'Preview failed'));
    } finally {
      setSaving(false);
    }
  }

  async function confirmSsh() {
    if (saving || !sshPreview) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await api<{ quarantined: boolean }>(`/v1/admin/vps-servers/${server.id}/ssh`, {
        method: 'PUT',
        body: { ...sshBody(), confirmation_token: sshPreview.confirmation_token },
      });
      setPrivateKey('');
      setPassword('');
      setShowForm(false);
      setSshPreview(null);
      flash(res.quarantined ? 'SSH access saved - quarantined until you pin the new host key' : 'SSH access saved');
      await onChanged();
    } catch (e) {
      setErr(sshError(e, 'Save failed'));
      setSshPreview(null);
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    if (testing) return;
    setTesting(true);
    setErr(null);
    setTestResult(null);
    setResult(null);
    try {
      const res = await api<TestResult>(`/v1/admin/vps-servers/${server.id}/ssh/test`, { method: 'POST' });
      setTestResult(res);
    } catch (e) {
      if (e instanceof ApiError) {
        setTestResult({ ...(e.body as TestResult), ok: false, connected: false });
        setErr(`Test failed (HTTP ${e.status}).`);
      } else {
        setErr('Test failed.');
      }
    } finally {
      setTesting(false);
    }
  }

  async function rotateKey() {
    if (rotating) return;
    setRotating(true);
    setErr(null);
    setRotateResult(null);
    try {
      const res = await api<RotateResult>(`/v1/admin/vps-servers/${server.id}/ssh/rotate`, {
        method: 'POST',
        body: coolifyKeyUuid.trim() ? { coolify_key_uuid: coolifyKeyUuid.trim() } : {},
      });
      setRotateResult(res);
      flash('SSH key rotated');
      setShowRotate(false);
      setCoolifyKeyUuid('');
      await onChanged();
    } catch (e) {
      if (e instanceof ApiError) {
        setRotateResult({ ...(e.body as RotateResult), ok: false });
        setErr(`Rotation failed (HTTP ${e.status}).`);
      } else {
        setErr('Rotation failed.');
      }
    } finally {
      setRotating(false);
    }
  }

  /**
   * OCP-542 - read the key the box currently presents. No credential is sent:
   * the handshake is aborted at key exchange, so inspecting a suspect host is
   * always safe.
   */
  async function scanHostKey() {
    if (scanning) return;
    setScanning(true);
    setErr(null);
    setScan(null);
    try {
      const res = await api<HostKeyScanResult>(`/v1/admin/vps-servers/${server.id}/ssh/host-key/scan`, {
        method: 'POST',
      });
      setScan(res);
    } catch (e) {
      setErr(e instanceof ApiError ? `Host-key scan failed (HTTP ${e.status}).` : 'Host-key scan failed.');
    } finally {
      setScanning(false);
    }
  }

  /**
   * OCP-542 - the deliberate (re-)pin. The fingerprint the operator just saw is
   * echoed back to the API, which re-reads the live key and refuses if it moved
   * again. Never wired to run automatically.
   */
  async function pinHostKey(fingerprint: string) {
    if (pinning) return;
    setPinning(true);
    setErr(null);
    try {
      await api(`/v1/admin/vps-servers/${server.id}/ssh/host-key/pin`, {
        method: 'POST',
        body: { confirm_fingerprint: fingerprint, reason: pinReason.trim() },
      });
      setScan(null);
      setPinReason('');
      setTestResult(null);
      flash('SSH host key pinned');
      await onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setErr('The host is presenting a different key than the one you confirmed. Scan again before pinning.');
      } else {
        setErr(sshError(e, 'Pin failed'));
      }
    } finally {
      setPinning(false);
    }
  }

  async function installAgent() {
    if (installing) return;
    setInstalling(true);
    setErr(null);
    setResult(null);
    try {
      const res = await api<InstallResult>(`/v1/admin/vps-servers/${server.id}/install-agent`, {
        method: 'POST',
      });
      setResult(res);
      flash(res.ok ? 'Agent installed' : 'Install finished with errors');
      await onChanged();
    } catch (e) {
      if (e instanceof ApiError) {
        // 502/409 carry a structured body; surface it.
        const body = (e.body ?? {}) as InstallResult;
        setResult({ ...body, ok: false });
        setErr(`Install failed (HTTP ${e.status}).`);
      } else {
        setErr('Install failed.');
      }
    } finally {
      setInstalling(false);
    }
  }

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">SSH &amp; host-agent</h2>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            {server.ssh_configured ? (
              <span className="text-gray-700 dark:text-gray-300">
                SSH configured ({server.ssh_auth_method}) —{' '}
                <span className="font-mono text-xs">
                  {server.ssh_user}@{server.ssh_host}:{server.ssh_port}
                </span>
              </span>
            ) : (
              <span className="text-gray-500">
                No SSH credential stored. Add a key or password to enable one-click agent install.
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              {server.ssh_configured ? 'Replace credential' : 'Add SSH credential'}
            </button>
            <button
              type="button"
              disabled={!server.ssh_configured || testing}
              onClick={() => void testConnection()}
              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
              title={server.ssh_configured ? 'Test the SSH connection' : 'Add a credential first'}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button
              type="button"
              disabled={!server.ssh_configured || installing}
              onClick={() => void installAgent()}
              className="rounded bg-brand-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              title={server.ssh_configured ? 'Run install.sh over SSH' : 'Add a credential first'}
            >
              {installing ? 'Installing…' : 'Install agent'}
            </button>
            <button
              type="button"
              disabled={!server.ssh_host || scanning}
              onClick={() => void scanHostKey()}
              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
              title={
                server.ssh_host
                  ? 'Read the host key this address presents right now (no credential is sent)'
                  : 'Set an SSH host first'
              }
              data-testid="host-key-scan"
            >
              {scanning ? 'Checking…' : 'Check host key'}
            </button>
            <button
              type="button"
              disabled={!server.ssh_configured || rotating}
              onClick={() => setShowRotate((s) => !s)}
              className="rounded border border-amber-300 dark:border-amber-700 px-2.5 py-1 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50"
              title="Generate a fresh key, swap it in safely (verify before cutover)"
            >
              Rotate key
            </button>
          </div>
        </div>

        {/*
          OCP-542 - the pinned host identity. Every SSH action verifies against
          this; a box that presents a different key fails closed until an
          operator deliberately re-pins.
        */}
        {server.ssh_endpoint_quarantined_at && (
          <div
            data-testid="ssh-quarantine"
            className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-800 dark:text-red-200"
          >
            SSH endpoint changed {fmtDate(server.ssh_endpoint_quarantined_at)} - QUARANTINED. OCP opens no credentialed
            connection to this server (no backup, restore, bootstrap, install, test) until you verify the new host key
            out of band and pin it: Check host key, then pin with a reason.
          </div>
        )}
        <div className="text-xs text-gray-600 dark:text-gray-400" data-testid="host-key-status">
          <span className="text-gray-400">Host key:</span>{' '}
          {server.ssh_host_key_fingerprint ? (
            <>
              <span className="font-mono break-all">{server.ssh_host_key_fingerprint}</span>
              {server.ssh_host_key_algo && <span className="ml-1.5 text-gray-400">{server.ssh_host_key_algo}</span>}
              {server.ssh_host_key_pinned_at && (
                <span className="ml-1.5 text-gray-400" title="When this host key was pinned">
                  pinned {fmtDate(server.ssh_host_key_pinned_at)}
                </span>
              )}
              {server.ssh_host_key_pin_source === 'operator_confirmed' ? (
                <span data-testid="host-key-provenance" className="ml-1.5 text-green-700 dark:text-green-400" title="A person compared the fingerprint and pinned it">
                  operator-confirmed
                </span>
              ) : (
                <span
                  data-testid="host-key-provenance"
                  className="ml-1.5 text-amber-700 dark:text-amber-300"
                  title="Pinned by trust on first use: whatever answered first. Not a verification - file-backup setup and restore need a confirmed pin (Check host key, then pin with a reason)."
                >
                  trust on first use - not verified
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-500">
              not pinned yet - the next connection pins whatever this address presents (trust on first use)
            </span>
          )}
        </div>

        {/* OCP-469 - host-agent build version + freshness against the shipped image build. */}
        <div className="text-xs text-gray-600 dark:text-gray-400" data-testid="agent-version-status">
          <span className="text-gray-400">Host-agent:</span>{' '}
          {server.agent_version ? (
            <>
              <span className="font-mono">v{server.agent_version}</span>
              {server.agent_outdated ? (
                <span
                  className="ml-1.5 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
                  title={`A newer host-agent (v${server.agent_current_version ?? '?'}) is baked into the running control-plane image. Re-run host bootstrap to converge this box.`}
                >
                  outdated
                </span>
              ) : server.agent_current_version ? (
                <span className="ml-1.5 text-green-600 dark:text-green-400">up to date</span>
              ) : null}
              {server.agent_current_version && (
                <span className="ml-1.5 text-gray-400">shipped v{server.agent_current_version}</span>
              )}
              {server.agent_version_seen_at && (
                <span className="ml-1.5 text-gray-400" title="When this agent build was first seen on this box">
                  since {fmtDate(server.agent_version_seen_at)}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-500">not reported yet (no beacon from a versioned agent)</span>
          )}
        </div>

        {err && (
          <div className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">
            {err}
          </div>
        )}

        {showForm && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-1">
              Host / IP
              <input
                value={host}
                onChange={(e) => sshEdited(setHost)(e.target.value)}
                placeholder="203.0.113.10"
                className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-400">
              User
              <input
                value={user}
                onChange={(e) => sshEdited(setUser)(e.target.value)}
                placeholder="root"
                className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-400">
              Port
              <input
                value={port}
                onChange={(e) => sshEdited(setPort)(e.target.value)}
                inputMode="numeric"
                className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
              />
            </label>
            <div className="sm:col-span-3 flex gap-3 text-xs text-gray-600 dark:text-gray-400">
              <label className="flex items-center gap-1">
                <input type="radio" name="ssh-auth" checked={authMethod === 'key'} onChange={() => sshEdited(setAuthMethod)('key')} />
                Private key
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" name="ssh-auth" checked={authMethod === 'password'} onChange={() => sshEdited(setAuthMethod)('password')} />
                Password
              </label>
            </div>
            {authMethod === 'key' ? (
              <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-3">
                Private key (PEM) — stored encrypted, never shown again. Unencrypted OpenSSH/PEM only (no .ppk, no passphrase).
                <textarea
                  value={privateKey}
                  onChange={(e) => sshEdited(setPrivateKey)(e.target.value)}
                  rows={5}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-mono text-xs"
                />
              </label>
            ) : (
              <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-3">
                Password — stored encrypted, never shown again
                <input
                  type="password"
                  value={password}
                  onChange={(e) => sshEdited(setPassword)(e.target.value)}
                  autoComplete="new-password"
                  placeholder="SSH password"
                  className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
                />
              </label>
            )}
            <div className="sm:col-span-3">
              {sshPreview && (
                <div data-testid="ssh-change-preview" className="mb-2 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
                  <div>
                    {sshPreview.proposed.ssh_user}@{sshPreview.proposed.ssh_host}:{sshPreview.proposed.ssh_port} presents{' '}
                    <span className="font-mono break-all">{sshPreview.observed_host_key.fingerprint}</span>{' '}
                    <span className="text-amber-700 dark:text-amber-300">{sshPreview.observed_host_key.algo}</span>
                  </div>
                  <div className="mt-1">{sshPreview.effect}</div>
                  <div className="mt-1 text-amber-700 dark:text-amber-300">Compare the fingerprint with the host before you confirm. The confirmation is valid once, for 10 minutes.</div>
                </div>
              )}
              {sshPreview ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void confirmSsh()}
                  className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Confirm SSH change'}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void saveSsh()}
                  className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {saving ? 'Checking…' : 'Preview SSH change'}
                </button>
              )}
              {server.ssh_user !== 'root' && (
                <span className="ml-2 text-[11px] text-gray-500">
                  Non-root user: install runs via <code>sudo</code> — passwordless sudo must be set up on the box.
                </span>
              )}
            </div>
          </div>
        )}

        {showRotate && (
          <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Generates a fresh ed25519 key, adds it to the box, verifies it connects, then cuts over — the old key
              stays valid until the new one is proven (no lockout).
            </p>
            <label className="block text-xs text-gray-600 dark:text-gray-400">
              Coolify key UUID (optional)
              <input
                value={coolifyKeyUuid}
                onChange={(e) => setCoolifyKeyUuid(e.target.value)}
                placeholder="if this key is shared with Coolify, paste its key UUID so Coolify is updated too"
                className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-mono text-xs"
              />
            </label>
            <p className="text-[11px] text-gray-500">
              Leave empty to rotate only OCP's access. If empty and the key is shared with Coolify, Coolify will lose
              access once the old key is removed.
            </p>
            <button
              type="button"
              disabled={rotating}
              onClick={() => void rotateKey()}
              className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {rotating ? 'Rotating…' : 'Rotate now'}
            </button>
          </div>
        )}

        {rotateResult && (
          <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs">
            {rotateResult.rotated ? (
              <span className="text-green-700 dark:text-green-400">
                Key rotated · Coolify: {rotateResult.coolify_updated === null ? 'not managed' : rotateResult.coolify_updated ? 'updated' : 'NOT updated'} · old key: {rotateResult.old_key_removed ? 'removed' : 'kept'}
                {rotateResult.message ? ` — ${rotateResult.message}` : ''}
              </span>
            ) : (
              <span className="text-red-700 dark:text-red-400">
                Rotation failed{rotateResult.stage ? ` at ${rotateResult.stage}` : ''}
                {rotateResult.reason ? ` (${rotateResult.reason})` : ''}
                {rotateResult.message ? `: ${rotateResult.message}` : ''} — the old key still works.
              </span>
            )}
          </div>
        )}

        {/* OCP-755 - key-only SSH: state, preflight, audited hardening run. */}
        <SshHardeningPanel server={server} onChanged={onChanged} flash={flash} />

        {/* OCP-542 - scan outcome + the deliberate re-pin decision. */}
        {scan && (
          <div
            className={`rounded border p-3 space-y-2 text-xs ${
              scan.matches === false
                ? 'border-red-300 dark:border-red-900/60 bg-red-50 dark:bg-red-900/25'
                : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950'
            }`}
            data-testid="host-key-scan-result"
          >
            {!scan.ok ? (
              <p className="text-red-700 dark:text-red-400">
                Could not read the host key{scan.reason ? ` (${scan.reason})` : ''}
                {scan.message ? `: ${scan.message}` : ''}
              </p>
            ) : scan.matches === true ? (
              <p className="text-green-700 dark:text-green-400">
                Host key matches the pin - <span className="font-mono break-all">{scan.observed?.fingerprint}</span>
              </p>
            ) : (
              <>
                {scan.matches === false ? (
                  <p className="font-medium text-red-800 dark:text-red-300">
                    Host key CHANGED. Until this is re-pinned, every SSH action on this box fails closed - no
                    credential and no script is sent. Treat it as an interception unless you know this host was
                    rebuilt or migrated.
                  </p>
                ) : (
                  <p className="text-gray-700 dark:text-gray-300">
                    Nothing is pinned yet. Confirm the fingerprint below (compare it against the provider console or
                    an out-of-band <code>ssh-keyscan</code>) to pin this host identity.
                  </p>
                )}
                <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 font-mono break-all">
                  {scan.pinned && (
                    <>
                      <dt className="text-gray-500">pinned</dt>
                      <dd>{scan.pinned.fingerprint}</dd>
                    </>
                  )}
                  <dt className="text-gray-500">presented</dt>
                  <dd>{scan.observed?.fingerprint}</dd>
                </dl>
                {scan.observed && (
                  <input
                    type="text"
                    value={pinReason}
                    onChange={(e) => setPinReason(e.target.value)}
                    aria-label="Pin reason"
                    placeholder="Where you verified the fingerprint (audited), e.g. provider console 2026-10-21"
                    className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-sans text-xs"
                  />
                )}
                {scan.observed && (
                  <button
                    type="button"
                    disabled={pinning || pinReason.trim().length < 10}
                    onClick={() => void pinHostKey(scan.observed!.fingerprint)}
                    className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                    data-testid="host-key-pin"
                  >
                    {pinning
                      ? 'Pinning…'
                      : scan.matches === false
                        ? 'Re-pin this host key (I verified the host was rebuilt)'
                        : 'Pin this host key'}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {testResult && (
          <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs">
            {testResult.connected ? (
              <span className="text-green-700 dark:text-green-400">
                Connected as <span className="font-mono">{testResult.user}</span>
                {testResult.sudo && testResult.sudo !== 'n/a' ? ` · sudo: ${testResult.sudo}` : ''}
                {testResult.sudo === 'needs-password' && (
                  <span className="text-yellow-700 dark:text-yellow-400"> — set up passwordless sudo or the install will fail</span>
                )}
              </span>
            ) : testResult.reason === 'host_key_mismatch' ? (
              // OCP-542 - the one connection failure that is a security event,
              // not a connectivity problem. Route the operator to the pin flow.
              <div className="space-y-1.5" data-testid="host-key-mismatch">
                <p className="font-medium text-red-800 dark:text-red-300">
                  Host key mismatch - the connection was aborted before anything was sent.
                </p>
                {testResult.host_key && (
                  <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 font-mono break-all text-[11px]">
                    <dt className="text-gray-500">pinned</dt>
                    <dd>{testResult.host_key.expected_fingerprint}</dd>
                    <dt className="text-gray-500">presented</dt>
                    <dd>{testResult.host_key.observed_fingerprint}</dd>
                  </dl>
                )}
                <p className="text-[11px] text-gray-600 dark:text-gray-400">
                  If this box was legitimately rebuilt or migrated, use "Check host key" and confirm the new
                  fingerprint. Otherwise stop and investigate - something else is answering this address.
                </p>
              </div>
            ) : (
              <span className="text-red-700 dark:text-red-400">
                Could not connect{testResult.reason ? ` (${testResult.reason})` : ''}
                {testResult.message ? `: ${testResult.message}` : ''}
              </span>
            )}
          </div>
        )}

        {result && (
          <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5">
            <div className="text-xs font-medium">
              {result.ok ? (
                <span className="text-green-700 dark:text-green-400">Install succeeded (exit 0)</span>
              ) : (
                <span className="text-red-700 dark:text-red-400">
                  Install failed{result.exit_code != null ? ` (exit ${result.exit_code})` : ''}
                  {result.reason ? ` — ${result.reason}` : ''}
                  {result.error ? ` — ${result.error}` : ''}
                </span>
              )}
            </div>
            {(result.stdout || result.stderr || result.message) && (
              <pre className="mt-1.5 max-h-48 overflow-auto whitespace-pre-wrap break-all text-[11px] text-gray-600 dark:text-gray-400">
                {[result.message, result.stdout, result.stderr].filter(Boolean).join('\n')}
              </pre>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * OCP-438 - re-run the host bootstrap on an already-registered server. Runs the
 * SAME host-prep sections as register (idempotent Docker install, real-hostname
 * detect, OS timezone apply) over the stored SSH credential, then triggers
 * Coolify `validateServer` + a bounded `is_usable` poll - one click to bring a
 * pre-registered / drifted box (the STRATO boxes that predated the register-time
 * bootstrap) to Coolify-usable without hand-SSH. The button is disabled with a
 * hint when no SSH credential is stored (the endpoint would 409). A Docker /
 * validate failure is shown inline (the endpoint returns 200 with `ok: false`).
 */
interface BootstrapResult {
  docker: 'installed' | 'already-present' | 'failed' | 'skipped';
  validation: 'validated' | 'pending' | 'failed' | 'skipped';
  is_usable: boolean;
  detected_hostname: string | null;
  timezone: 'set' | 'failed' | 'unsupported' | 'skipped';
  applied_timezone: string | null;
  effective_timezone: string;
  timezone_source: 'override' | 'region' | 'default';
  // OCP-451 - GHCR registry login + pull-smoke outcome.
  registry_login?: 'ok' | 'failed' | 'skipped';
  registry_pull?: 'ok' | 'failed' | 'skipped';
  // OCP-462 - branded maintenance-page (orboto-maintenance) setup outcome.
  maintenance_page?: 'ok' | 'failed' | 'skipped';
  // OCP-469 - host-agent convergence outcome + the version the box reported.
  agent?: 'ok' | 'already-current' | 'failed' | 'skipped';
  agent_version?: string | null;
  // OCP-725 - cryptsetup + dm-crypt for encrypted tenant volumes (non-fatal).
  volume_crypto?: 'present' | 'installed' | 'failed' | 'skipped';
  // OCP-470 - privilege-escalation outcome. `unavailable` = the non-root SSH
  // user lacks passwordless sudo -> grant NOPASSWD (the actionable signal).
  sudo?: 'not-required' | 'ok' | 'unavailable' | 'skipped';
  detail: string;
  ok: boolean;
}

const DOCKER_LABELS: Record<BootstrapResult['docker'], string> = {
  installed: 'Docker installed',
  'already-present': 'Docker already present',
  failed: 'Docker install failed',
  skipped: 'Docker skipped',
};

const VALIDATION_LABELS: Record<BootstrapResult['validation'], string> = {
  validated: 'Coolify validated (usable)',
  pending: 'Coolify validation pending',
  failed: 'Coolify validation failed',
  skipped: 'Coolify validation skipped',
};

const TZ_STATUS_LABELS: Record<BootstrapResult['timezone'], string> = {
  set: 'OS timezone set',
  failed: 'OS timezone failed',
  unsupported: 'OS timezone unsupported (non-systemd host)',
  skipped: 'OS timezone skipped',
};

// OCP-451 - GHCR login + pull-smoke labels.
const REGISTRY_LOGIN_LABELS: Record<NonNullable<BootstrapResult['registry_login']>, string> = {
  ok: 'ghcr.io login OK',
  failed: 'ghcr.io login failed',
  skipped: 'ghcr.io login skipped (creds unconfigured)',
};

const REGISTRY_PULL_LABELS: Record<NonNullable<BootstrapResult['registry_pull']>, string> = {
  ok: 'tenant image pull OK',
  failed: 'tenant image pull failed',
  skipped: 'tenant image pull skipped',
};

// OCP-462 - branded maintenance-page (orboto-maintenance) setup labels.
const MAINTENANCE_LABELS: Record<NonNullable<BootstrapResult['maintenance_page']>, string> = {
  ok: 'maintenance page OK',
  failed: 'maintenance page failed (non-fatal)',
  skipped: 'maintenance page skipped',
};

// OCP-725 - cryptsetup + dm-crypt labels.
const VOLUME_CRYPTO_LABELS: Record<NonNullable<BootstrapResult['volume_crypto']>, string> = {
  present: 'cryptsetup + dm-crypt present',
  installed: 'cryptsetup installed',
  failed: 'cryptsetup / dm-crypt failed (encrypted volumes cannot open on this host)',
  skipped: 'volume encryption check skipped',
};

// OCP-469 - host-agent convergence labels.
const AGENT_LABELS: Record<NonNullable<BootstrapResult['agent']>, string> = {
  ok: 'host-agent updated + restarted',
  'already-current': 'host-agent already current',
  failed: 'host-agent update failed (non-fatal)',
  skipped: 'host-agent skipped (no agent installed - use Install agent)',
};

// OCP-470 - privilege-escalation labels. `unavailable` is the actionable
// "grant NOPASSWD sudo" case for a non-root SSH user; `not-required` (root) and
// `skipped` (SSH never ran) are not surfaced as their own line.
const SUDO_LABELS: Record<NonNullable<BootstrapResult['sudo']>, string> = {
  'not-required': 'root SSH user - no sudo needed',
  ok: 'non-root SSH user - sudo -n OK',
  unavailable: 'non-root SSH user - passwordless sudo denied',
  skipped: 'sudo state unknown (SSH did not run)',
};

function HostBootstrapPanel({
  server,
  onChanged,
  flash,
}: {
  server: ServerMeta;
  onChanged: () => Promise<void> | void;
  flash: (msg: string) => void;
}) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BootstrapResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runBootstrap = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setErr(null);
    setResult(null);
    try {
      const res = await api<{ server: ServerMeta; bootstrap: BootstrapResult }>(
        `/v1/admin/vps-servers/${server.id}/bootstrap`,
        { method: 'POST' },
      );
      setResult(res.bootstrap);
      flash(res.bootstrap.ok ? 'Host bootstrap complete' : 'Host bootstrap finished with issues');
      await onChanged();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body ?? {}) as { error?: string; message?: string };
        // 409 in-flight / no-credential + any structured message.
        setErr(body.message ?? `Bootstrap failed (HTTP ${e.status}).`);
      } else {
        setErr('Bootstrap failed.');
      }
    } finally {
      setRunning(false);
    }
  }, [running, server.id, flash, onChanged]);

  return (
    <section data-testid="host-bootstrap-panel">
      <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Host bootstrap</h2>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Re-run Docker install (idempotent) + hostname detect + OS timezone + host-agent
            convergence, then trigger Coolify validation. Makes a pre-registered or drifted box
            usable without manual SSH, and updates an out-of-date host-agent to the shipped build.
          </p>
          <button
            type="button"
            data-testid="host-bootstrap-run"
            disabled={!server.ssh_configured || running}
            onClick={() => void runBootstrap()}
            className="shrink-0 rounded bg-brand-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            title={
              server.ssh_configured
                ? 'Run Docker install + hostname + timezone + Coolify validate over the stored SSH credential'
                : 'Add an SSH credential first (SSH & host-agent above)'
            }
          >
            {running ? 'Bootstrapping…' : 'Re-run host bootstrap'}
          </button>
        </div>

        {!server.ssh_configured && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No SSH credential stored - add a key or password in SSH &amp; host-agent above to enable
            host bootstrap.
          </p>
        )}

        {err && (
          <div className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">
            {err}
          </div>
        )}

        {result && (
          <div
            data-testid="host-bootstrap-result"
            className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs space-y-1"
          >
            <div className="font-medium">
              {result.ok ? (
                <span className="text-green-700 dark:text-green-400">
                  Bootstrap OK{result.is_usable ? ' - server is Coolify-usable' : ''}
                </span>
              ) : (
                <span className="text-red-700 dark:text-red-400">Bootstrap finished with issues</span>
              )}
            </div>
            {/* OCP-470 - actionable banner: a non-root SSH user without
                passwordless sudo cannot run any privileged section. Tell the
                operator exactly what to fix instead of a raw permission-denied. */}
            {result.sudo === 'unavailable' && (
              <div
                data-testid="host-bootstrap-sudo-unavailable"
                className="rounded border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300"
              >
                Privileged steps could not escalate: the non-root SSH user
                (<span className="font-mono">{server.ssh_user}</span>) lacks
                passwordless sudo. Grant it{' '}
                <span className="font-mono">NOPASSWD</span> sudo (e.g. a{' '}
                <span className="font-mono">/etc/sudoers.d</span> drop-in), or register this box
                with a root SSH user, then re-run host bootstrap.
              </div>
            )}
            <ul className="space-y-0.5 text-gray-600 dark:text-gray-400">
              <li>
                <span className="text-gray-400">Docker:</span> {DOCKER_LABELS[result.docker]}
              </li>
              {result.sudo && result.sudo !== 'not-required' && (
                <li>
                  <span className="text-gray-400">Privileges:</span>{' '}
                  <span
                    className={
                      result.sudo === 'unavailable' ? 'text-amber-600 dark:text-amber-400' : undefined
                    }
                  >
                    {SUDO_LABELS[result.sudo]}
                  </span>
                </li>
              )}
              <li>
                <span className="text-gray-400">Coolify:</span> {VALIDATION_LABELS[result.validation]}
                {' · '}usable: {result.is_usable ? 'yes' : 'no'}
              </li>
              <li>
                <span className="text-gray-400">Timezone:</span> {TZ_STATUS_LABELS[result.timezone]}
                {result.applied_timezone ? ` (${result.applied_timezone})` : ''} · effective{' '}
                <span className="font-mono">{result.effective_timezone}</span> ({result.timezone_source})
              </li>
              {result.registry_login && result.registry_pull && (
                <li>
                  <span className="text-gray-400">Registry:</span>{' '}
                  <span
                    className={
                      result.registry_login === 'failed' ? 'text-red-600 dark:text-red-400' : undefined
                    }
                  >
                    {REGISTRY_LOGIN_LABELS[result.registry_login]}
                  </span>
                  {' · '}
                  <span
                    className={
                      result.registry_pull === 'failed' ? 'text-red-600 dark:text-red-400' : undefined
                    }
                  >
                    {REGISTRY_PULL_LABELS[result.registry_pull]}
                  </span>
                </li>
              )}
              {result.maintenance_page && (
                <li>
                  <span className="text-gray-400">Maintenance page:</span>{' '}
                  <span
                    className={
                      result.maintenance_page === 'failed' ? 'text-amber-600 dark:text-amber-400' : undefined
                    }
                  >
                    {MAINTENANCE_LABELS[result.maintenance_page]}
                  </span>
                </li>
              )}
              {result.agent && (
                <li>
                  <span className="text-gray-400">Host agent:</span>{' '}
                  <span
                    className={
                      result.agent === 'failed' ? 'text-amber-600 dark:text-amber-400' : undefined
                    }
                  >
                    {AGENT_LABELS[result.agent]}
                  </span>
                  {result.agent_version && (
                    <span className="ml-1 font-mono text-gray-500">v{result.agent_version}</span>
                  )}
                </li>
              )}
              {result.volume_crypto && (
                <li>
                  <span className="text-gray-400">Volume encryption:</span>{' '}
                  <span
                    className={
                      result.volume_crypto === 'failed' ? 'text-amber-600 dark:text-amber-400' : undefined
                    }
                  >
                    {VOLUME_CRYPTO_LABELS[result.volume_crypto]}
                  </span>
                </li>
              )}
              {result.detected_hostname && (
                <li>
                  <span className="text-gray-400">Detected hostname:</span>{' '}
                  <span className="font-mono">{result.detected_hostname}</span>
                  {result.detected_hostname !== server.hostname && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">
                      (differs from stored - edit the host field above to apply)
                    </span>
                  )}
                </li>
              )}
            </ul>
            {result.detail && (
              <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-all text-[11px] text-gray-500 dark:text-gray-500">
                {result.detail}
              </pre>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * OCP-426 - host OS patching panel: scan for pending updates, run a full patch
 * (no reboot), and trigger a controlled reboot (confirm + maintenance-window
 * gated for a tenant host). Mirrors the HostBootstrapPanel POST-action pattern.
 */
interface HostScanResult {
  status: 'ok' | 'failed' | 'skipped';
  skipReason: 'not-debian' | 'needs-root' | 'ssh-failed' | null;
  packagesAvailable: number | null;
  securityAvailable: number | null;
  rebootRequired: boolean;
  detail: string;
}
interface HostPatchResult {
  status: 'ok' | 'failed' | 'skipped';
  skipReason: 'not-debian' | 'needs-root' | 'ssh-failed' | null;
  packagesUpdated: number | null;
  packagesAvailable: number | null;
  securityAvailable: number | null;
  // OCP-503: packages held/phased even under dist-upgrade (apt "N not upgraded").
  keptBack: number | null;
  rebootRequired: boolean;
  // OCP-504: host-agent convergence folded into the patch (reuse OCP-469). The
  // patch also updates the box's report agent - no separate re-bootstrap.
  agent?: 'ok' | 'already-current' | 'failed' | 'skipped';
  agentVersion?: string | null;
  // OCP-807: coolify-proxy restart + socket check after a docker engine upgrade (OCP-806).
  proxyAfterDockerUpgrade?: 'restarted' | 'not_needed' | 'failed' | null;
  proxyAfterDockerUpgradeDetail?: string | null;
  outputTail: string;
  detail: string;
}
interface HostRebootResult {
  issued: boolean;
  health: 'green' | 'pending' | 'failed';
  containers_running: number | null;
  tenant_count: number;
  single_box_downtime: boolean;
  drain_restored: boolean;
  note: string;
  detail: string;
}

function fmtPatchDate(iso?: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

// OCP-504 - short host-agent phrase for the patch flash. `skipped` (no agent on
// the box) is the common no-op, so it is omitted from the one-liner (the result
// block still shows it); the other outcomes are surfaced.
const PATCH_AGENT_FLASH: Record<'ok' | 'already-current' | 'failed', string> = {
  ok: 'host-agent updated',
  'already-current': 'host-agent current',
  failed: 'host-agent update failed (non-fatal)',
};

/**
 * OCP-503 - one-line patch summary for the result flash. When packages remain
 * kept back even under the default dist-upgrade (phased rollouts / apt-mark
 * holds) the summary says "N kept back (held/phased)" instead of a bare "Patch
 * ok", so the operator is not left staring at a no-op that looks successful.
 * OCP-504 - appends the host-agent convergence outcome (the patch also updates
 * the box's report agent), skipping the no-op `skipped` case.
 */
function formatPatchSummary(patch: HostPatchResult): string {
  if (patch.status !== 'ok') {
    return `Patch ${patch.status}${patch.skipReason ? ` (${patch.skipReason})` : ''}`;
  }
  let msg = `Patched ${patch.packagesUpdated ?? 0} package(s), ${patch.packagesAvailable ?? 0} still available`;
  if ((patch.keptBack ?? 0) > 0) msg += `, ${patch.keptBack} kept back (held/phased)`;
  if (patch.rebootRequired) msg += ' - reboot pending';
  if (patch.agent && patch.agent !== 'skipped') msg += `; ${PATCH_AGENT_FLASH[patch.agent]}`;
  if (patch.proxyAfterDockerUpgrade === 'restarted') msg += '; coolify-proxy restarted after the docker upgrade';
  if (patch.proxyAfterDockerUpgrade === 'failed') msg += '; coolify-proxy still has no docker socket - check the host';
  return msg;
}

interface DiskEntry {
  path: string;
  bytes: number;
}
interface DiskReport {
  status: 'ok' | 'failed' | 'skipped';
  skipReason: string | null;
  filesystems: Array<{ mount: string; totalBytes: number; usedBytes: number }>;
  topLevel: DiskEntry[];
  secondLevel: DiskEntry[];
  /** OCP-609 - older reports (pre-0.76) lack these; render defensively. */
  thirdLevel?: DiskEntry[];
  hotspot?: { path: string; bytes: number; children: DiskEntry[] } | null;
  fileCounts?: Array<{ path: string; files: number }>;
  extraMounts: DiskEntry[];
  docker: {
    summary: Array<{ type: string; count: number; sizeBytes: number; reclaimableBytes: number }>;
    containers: DiskEntry[];
    volumes: DiskEntry[];
    images: DiskEntry[];
    /** OCP-639 - reports before 0.88 lack these; render defensively. */
    imagesTotalBytes?: number;
    imagesReclaimableBytes?: number;
  } | null;
  largeFiles: DiskEntry[];
  detail: string;
  generatedAt: string;
}
interface DiskGrowthRow {
  mount: string;
  alerting: boolean;
  last_alerted_at: string | null;
  last_reason: string | null;
  growth_gb_per_day: number | null;
  baseline_gb_per_day: number | null;
  days_to_full: number | null;
  updated_at: string;
}
interface DiskReportResponse {
  server_id: string;
  report: DiskReport | null;
  generated_at: string | null;
  growth: DiskGrowthRow[];
}

function fmtBytes(b: number): string {
  if (b >= 1e12) return `${(b / 1e12).toFixed(2)} TB`;
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(0)} MB`;
  return `${(b / 1e3).toFixed(0)} kB`;
}

/**
 * OCP-605 - "Disk usage" panel: the last persisted df/du/docker report (or run
 * a fresh one over SSH) and the per-mount abnormal-growth state.
 */
function HostDiskPanel({ server, flash }: { server: ServerMeta; flash: (msg: string) => void }) {
  const [data, setData] = useState<DiskReportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<DiskReportResponse>(`/v1/admin/vps-servers/${server.id}/disk-report`);
      // Defensive: an older api (or a test double) without the endpoint shape.
      setData({ server_id: server.id, report: res?.report ?? null, generated_at: res?.generated_at ?? null, growth: Array.isArray(res?.growth) ? res.growth : [] });
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load the disk report (HTTP ${e.status}).` : 'Could not load the disk report.');
    }
  }, [server.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api<DiskReportResponse>(`/v1/admin/vps-servers/${server.id}/disk-report`, { method: 'POST' });
      setData({ server_id: server.id, report: res?.report ?? null, generated_at: res?.generated_at ?? null, growth: Array.isArray(res?.growth) ? res.growth : [] });
      flash(res.report?.status === 'ok' ? 'Disk report updated.' : `Disk report ${res.report?.status ?? 'failed'}${res.report?.skipReason ? ` (${res.report.skipReason})` : ''}`);
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `Disk report failed (HTTP ${e.status}).`) : 'Disk report failed.');
    } finally {
      setBusy(false);
    }
  }

  const report = data?.report ?? null;
  const rows = (entries: DiskEntry[], testId: string, max = 12) => (
    <ul data-testid={testId} className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
      {entries.slice(0, max).map((e) => (
        <li key={e.path} className="flex justify-between gap-3 py-0.5">
          <span className="truncate font-mono text-gray-700 dark:text-gray-300" title={e.path}>{e.path}</span>
          <span className="shrink-0 tabular-nums text-gray-600 dark:text-gray-400">{fmtBytes(e.bytes)}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <section data-testid="host-disk-panel" className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Disk usage</h2>
        <div className="flex items-center gap-2">
          {data?.generated_at && (
            <span className="text-[11px] text-gray-500">last report {fmtDateTime(data.generated_at)}</span>
          )}
          <button
            type="button"
            data-testid="host-disk-report-run"
            disabled={!server.ssh_configured || busy}
            onClick={() => void run()}
            className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            title="Run df / du / docker sizes over SSH (read-only, can take minutes on a large disk)."
          >
            {busy ? 'Running…' : 'Run disk report'}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-2 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">{err}</div>
      )}

      {data && data.growth.length > 0 && (
        <div data-testid="host-disk-growth" className="mb-3 space-y-1">
          {data.growth.map((g) => (
            <div
              key={g.mount}
              className={`rounded px-2.5 py-1.5 text-xs ${g.alerting ? 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200' : 'bg-gray-50 text-gray-700 dark:bg-gray-800/60 dark:text-gray-300'}`}
            >
              <span className="font-mono">{g.mount}</span>
              {' '}
              {g.growth_gb_per_day == null ? 'no 24 h figure yet' : `${g.growth_gb_per_day >= 0 ? '+' : ''}${g.growth_gb_per_day.toFixed(1)} GB / 24 h`}
              {g.baseline_gb_per_day != null && ` (baseline ${g.baseline_gb_per_day.toFixed(1)} GB/day)`}
              {g.days_to_full != null && `, full in ~${g.days_to_full.toFixed(0)} days`}
              {g.alerting && <span className="ml-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-medium uppercase text-white">abnormal</span>}
              {g.alerting && g.last_reason && <span className="ml-1 text-[11px]">{g.last_reason}</span>}
            </div>
          ))}
        </div>
      )}

      {!report && data && (
        <p className="text-xs text-gray-500">No disk report yet - run one to see what is on this disk.</p>
      )}

      {report && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Filesystems</h3>
            <ul data-testid="host-disk-filesystems" className="text-xs">
              {report.filesystems.map((f) => (
                <li key={f.mount} className="flex justify-between gap-3 py-0.5">
                  <span className="font-mono text-gray-700 dark:text-gray-300">{f.mount}</span>
                  <span className="tabular-nums text-gray-600 dark:text-gray-400">
                    {fmtBytes(f.usedBytes)} / {fmtBytes(f.totalBytes)} ({f.totalBytes > 0 ? Math.round((f.usedBytes / f.totalBytes) * 100) : 0}%)
                  </span>
                </li>
              ))}
            </ul>
            <h3 className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">Largest directories (2 levels)</h3>
            {rows(report.secondLevel, 'host-disk-second-level', 15)}
            {report.hotspot && report.hotspot.children.length > 0 && (
              <>
                <h3 className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Inside {report.hotspot.path} ({fmtBytes(report.hotspot.bytes)})
                </h3>
                {rows(report.hotspot.children, 'host-disk-hotspot', 10)}
              </>
            )}
            {report.fileCounts && report.fileCounts.length > 0 && (
              <>
                <h3 className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">File counts</h3>
                <ul data-testid="host-disk-file-counts" className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
                  {report.fileCounts.slice(0, 8).map((e) => (
                    <li key={e.path} className="flex justify-between gap-3 py-0.5">
                      <span className="truncate font-mono text-gray-700 dark:text-gray-300" title={e.path}>{e.path}</span>
                      <span className="shrink-0 tabular-nums text-gray-600 dark:text-gray-400">{e.files.toLocaleString('en-US')} files</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {report.extraMounts.length > 0 && (
              <>
                <h3 className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">Data mounts</h3>
                {rows(report.extraMounts, 'host-disk-extra-mounts')}
              </>
            )}
          </div>
          <div>
            {report.docker ? (
              <>
                <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Docker</h3>
                <ul className="text-xs">
                  {report.docker.summary.map((s) => (
                    <li key={s.type} className="flex justify-between gap-3 py-0.5">
                      <span className="text-gray-700 dark:text-gray-300">{s.type} ({s.count})</span>
                      <span className="tabular-nums text-gray-600 dark:text-gray-400">{fmtBytes(s.sizeBytes)}{s.reclaimableBytes > 0 ? `, ${fmtBytes(s.reclaimableBytes)} reclaimable` : ''}</span>
                    </li>
                  ))}
                </ul>
                {report.docker.imagesTotalBytes != null && (
                  /* OCP-639 - the Images row lifted out + where the reclaim happens. */
                  <p data-testid="host-disk-images-usage" className="mt-1 text-[11px] text-gray-500">
                    Images use {fmtBytes(report.docker.imagesTotalBytes)}
                    {(report.docker.imagesReclaimableBytes ?? 0) > 0 ? `, ${fmtBytes(report.docker.imagesReclaimableBytes ?? 0)} unused - see Host cleanup below.` : ', nothing unused.'}
                  </p>
                )}
                {report.docker.volumes.length > 0 && (
                  <>
                    <h4 className="mt-2 text-[11px] text-gray-500">Volumes</h4>
                    {rows(report.docker.volumes, 'host-disk-volumes', 8)}
                  </>
                )}
                {report.docker.containers.length > 0 && (
                  <>
                    <h4 className="mt-2 text-[11px] text-gray-500">Container writable layers</h4>
                    {rows(report.docker.containers, 'host-disk-containers', 8)}
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-500">No Docker on this host.</p>
            )}
            {report.largeFiles.length > 0 && (
              <>
                <h3 className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">Files over 200 MB</h3>
                {rows(report.largeFiles, 'host-disk-large-files', 10)}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ---- OCP-639 host cleanup -----------------------------------------------------

interface CleanupImage {
  repository: string;
  tag: string;
  id: string;
  size_bytes: number;
  created_at: string | null;
}
interface CleanupPlan {
  status: 'ok' | 'failed' | 'skipped';
  skip_reason: string | null;
  images: CleanupImage[];
  image_ids: string[];
  kept: CleanupImage[];
  in_use_skipped: number;
  dangling: number;
  build_cache_bytes: number;
  journal_bytes: number;
  journal_max_bytes: number;
  disk_used_bytes: number;
  disk_total_bytes: number;
  projected_reclaim_bytes: number;
  docker: boolean;
  detail: string;
  generated_at: string;
}
interface CleanupExecuteResult {
  status: 'ok' | 'failed' | 'skipped';
  skip_reason: string | null;
  removed: string[];
  removed_tags: string[];
  failed: Array<{ id: string; error: string }>;
  reclaimed_bytes: number;
  prune_reclaimed_bytes: number;
  build_cache_reclaimed_bytes: number;
  journal_after_bytes: number;
  disk_used_before_bytes: number;
  disk_used_after_bytes: number;
  disk_total_bytes: number;
  detail: string;
  generated_at: string;
}
interface CleanupRun {
  id: string;
  mode: 'dry_run' | 'execute';
  trigger: 'operator' | 'roll';
  source: 'mcp' | 'admin-ui' | 'system';
  status: 'running' | 'ok' | 'failed' | 'skipped';
  started_at: string;
  finished_at: string | null;
  maintenance_window_id: string | null;
  result: (Partial<CleanupPlan> & Partial<CleanupExecuteResult> & { reason?: string }) | null;
  error: string | null;
}
interface CleanupPlanResponse {
  server_id: string;
  mode: 'dry_run';
  run: CleanupRun;
  plan: CleanupPlan;
}
interface CleanupExecuteResponse {
  server_id: string;
  mode: 'execute';
  run: CleanupRun;
  result: CleanupExecuteResult;
}
interface CleanupListResponse {
  server_id: string;
  ssh_applicable: boolean;
  runs: CleanupRun[];
}

function cleanupRunSummary(r: CleanupRun): string {
  if (r.error) return r.error;
  const res = r.result;
  if (!res) return r.status === 'running' ? 'running' : '';
  if (r.mode === 'dry_run') {
    return `${res.image_ids?.length ?? 0} image(s), projected ${fmtBytes(res.projected_reclaim_bytes ?? 0)}, ${res.dangling ?? 0} dangling, journal ${fmtBytes(res.journal_bytes ?? 0)}`;
  }
  if (res.reason) return res.reason.replace(/_/g, ' ');
  return `removed ${res.removed?.length ?? 0}, failed ${res.failed?.length ?? 0}, reclaimed ${fmtBytes(res.reclaimed_bytes ?? 0)}`;
}

/**
 * OCP-639 - "Host cleanup" card: Plan cleanup (dry run over SSH, nothing
 * removed) shows the unused orboto images beyond the two newest tags per
 * repository with tags + sizes, dangling count, build cache, journal and the
 * projected reclaim; Run cleanup is enabled only after a plan, confirms, and
 * executes exactly that plan's image ids. Last runs (operator + after-roll)
 * below. The agent-only notice replaces this card on a host without SSH.
 */
function HostCleanupPanel({ server, flash }: { server: ServerMeta; flash: (msg: string) => void }) {
  const [plan, setPlan] = useState<CleanupPlan | null>(null);
  const [result, setResult] = useState<CleanupExecuteResult | null>(null);
  const [runs, setRuns] = useState<CleanupRun[]>([]);
  const [busy, setBusy] = useState<null | 'plan' | 'run'>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      const res = await api<CleanupListResponse>(`/v1/admin/vps-servers/${server.id}/cleanup`);
      setRuns(Array.isArray(res?.runs) ? res.runs : []);
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load the cleanup runs (HTTP ${e.status}).` : 'Could not load the cleanup runs.');
    }
  }, [server.id]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const apiMessage = (e: unknown, fallback: string) =>
    e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `${fallback} (HTTP ${e.status}).`) : `${fallback}.`;

  async function planCleanup() {
    if (busy) return;
    setBusy('plan');
    setErr(null);
    setResult(null);
    try {
      const res = await api<CleanupPlanResponse>(`/v1/admin/vps-servers/${server.id}/cleanup`, { method: 'POST', body: { dry_run: true } });
      setPlan(res.plan);
      flash(
        res.plan.status === 'ok'
          ? `Cleanup plan: ${res.plan.image_ids.length} image(s), ${fmtBytes(res.plan.projected_reclaim_bytes)} projected`
          : `Cleanup plan ${res.plan.status}${res.plan.skip_reason ? ` (${res.plan.skip_reason})` : ''}`,
      );
      await loadRuns();
    } catch (e) {
      // 424 = the script did not finish; the body still carries the plan row.
      if (e instanceof ApiError && (e.body as { plan?: CleanupPlan })?.plan) setPlan((e.body as { plan: CleanupPlan }).plan);
      setErr(apiMessage(e, 'Cleanup plan failed'));
    } finally {
      setBusy(null);
    }
  }

  async function runCleanup() {
    if (busy || !plan || plan.status !== 'ok') return;
    const tags = plan.images.map((i) => `${i.repository.replace(/^ghcr\.io\/orboto\//, '')}:${i.tag}`);
    if (!window.confirm(
      `Run the cleanup on ${server.name ?? server.hostname}?\n\nRemoves ${plan.image_ids.length} image(s)${tags.length ? `: ${tags.join(', ')}` : ''}, prunes ${plan.dangling} dangling image(s)${plan.build_cache_bytes > 0 ? `, ${fmtBytes(plan.build_cache_bytes)} build cache` : ''} and caps the journal at ${fmtBytes(plan.journal_max_bytes)}. Projected reclaim ${fmtBytes(plan.projected_reclaim_bytes)}.\n\nNever touched: images in use, the two newest tags per orboto image, non-orboto images, volumes.\n\nProceed?`,
    )) {
      return;
    }
    setBusy('run');
    setErr(null);
    try {
      const res = await api<CleanupExecuteResponse>(`/v1/admin/vps-servers/${server.id}/cleanup`, {
        method: 'POST',
        body: { dry_run: false, confirm: true, image_ids: plan.image_ids },
      });
      setResult(res.result);
      // A fresh plan is required for the next run - the box changed.
      setPlan(null);
      flash(
        res.result.status === 'ok'
          ? `Cleanup done: ${fmtBytes(res.result.reclaimed_bytes)} reclaimed, ${res.result.removed.length} image(s) removed${res.result.failed.length ? `, ${res.result.failed.length} failed` : ''}`
          : `Cleanup ${res.result.status}${res.result.skip_reason ? ` (${res.result.skip_reason})` : ''}`,
      );
      await loadRuns();
    } catch (e) {
      if (e instanceof ApiError && (e.body as { result?: CleanupExecuteResult })?.result) {
        setResult((e.body as { result: CleanupExecuteResult }).result);
        setPlan(null);
        await loadRuns();
      }
      setErr(apiMessage(e, 'Cleanup failed'));
    } finally {
      setBusy(null);
    }
  }

  const shortRepo = (repository: string) => repository.replace(/^ghcr\.io\/orboto\//, '');

  return (
    <section data-testid="host-cleanup-panel" className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Host cleanup</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="host-cleanup-plan"
            disabled={!server.ssh_configured || busy !== null}
            onClick={() => void planCleanup()}
            className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            title="Dry run over SSH: lists unused orboto images beyond the two newest tags, dangling images, build cache and journal size. Removes nothing."
          >
            {busy === 'plan' ? 'Planning…' : 'Plan cleanup'}
          </button>
          <button
            type="button"
            data-testid="host-cleanup-run"
            disabled={!server.ssh_configured || busy !== null || !plan || plan.status !== 'ok'}
            onClick={() => void runCleanup()}
            className="rounded bg-gray-900 dark:bg-gray-100 px-2.5 py-1 text-xs font-medium text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-300 disabled:opacity-50"
            title={plan ? 'Executes exactly the plan above (confirm first).' : 'Plan first - the run executes exactly what the plan shows.'}
          >
            {busy === 'run' ? 'Cleaning…' : 'Run cleanup'}
          </button>
        </div>
      </div>
      <p className="mb-2 text-xs text-gray-500">
        Removes orboto images no container uses (keeping the two newest tags per image for a rollback), dangling
        images, the build cache and journal above the cap. Never images in use, non-orboto images or volumes. A
        maintenance-window roll runs this automatically per host.
      </p>

      {err && (
        <div className="mb-2 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">{err}</div>
      )}

      {plan && (
        <div data-testid="host-cleanup-plan-result" className="mb-3 rounded border border-gray-200 dark:border-gray-800 p-3 text-xs">
          {plan.status !== 'ok' ? (
            <p className="text-amber-700 dark:text-amber-400">Plan {plan.status}{plan.skip_reason ? ` (${plan.skip_reason})` : ''}.</p>
          ) : (
            <>
              <p className="font-medium text-gray-800 dark:text-gray-200">
                Projected reclaim <span data-testid="host-cleanup-projected">{fmtBytes(plan.projected_reclaim_bytes)}</span>
                {' - '}{plan.image_ids.length} image(s), {plan.dangling} dangling, build cache {fmtBytes(plan.build_cache_bytes)}, journal {fmtBytes(plan.journal_bytes)} (cap {fmtBytes(plan.journal_max_bytes)})
                {plan.disk_total_bytes > 0 && `; disk ${fmtBytes(plan.disk_used_bytes)} / ${fmtBytes(plan.disk_total_bytes)} used`}
                {!plan.docker && '; no Docker on this host'}
              </p>
              {plan.images.length > 0 ? (
                <ul data-testid="host-cleanup-images" className="mt-1 divide-y divide-gray-100 dark:divide-gray-800">
                  {plan.images.map((i) => (
                    <li key={`${i.id}:${i.tag}`} className="flex justify-between gap-3 py-0.5">
                      <span className="truncate font-mono text-gray-700 dark:text-gray-300" title={i.id}>{shortRepo(i.repository)}:{i.tag}</span>
                      <span className="shrink-0 tabular-nums text-gray-600 dark:text-gray-400">{fmtBytes(i.size_bytes)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-gray-500">No unused orboto image beyond the two newest tags.</p>
              )}
              {(plan.kept.length > 0 || plan.in_use_skipped > 0) && (
                <p className="mt-1 text-[11px] text-gray-500">
                  Kept: {plan.kept.map((k) => `${shortRepo(k.repository)}:${k.tag}`).join(', ')}
                  {plan.in_use_skipped > 0 && ` - ${plan.in_use_skipped} older image(s) still in use by a container, kept.`}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {result && (
        <div data-testid="host-cleanup-result" className={`mb-3 rounded border p-3 text-xs ${result.status === 'ok' && result.failed.length === 0 ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20' : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20'}`}>
          <p className="font-medium text-gray-800 dark:text-gray-200">
            {result.status === 'ok' ? 'Cleanup done' : `Cleanup ${result.status}${result.skip_reason ? ` (${result.skip_reason})` : ''}`}
            {' - '}reclaimed <span data-testid="host-cleanup-reclaimed">{fmtBytes(result.reclaimed_bytes)}</span>
            {' '}({fmtBytes(result.disk_used_before_bytes)} to {fmtBytes(result.disk_used_after_bytes)} used), {result.removed.length} image(s) removed
            {result.prune_reclaimed_bytes > 0 && `, dangling ${fmtBytes(result.prune_reclaimed_bytes)}`}
            {result.build_cache_reclaimed_bytes > 0 && `, build cache ${fmtBytes(result.build_cache_reclaimed_bytes)}`}
            , journal now {fmtBytes(result.journal_after_bytes)}
          </p>
          {result.removed_tags.length > 0 && (
            <p className="mt-1 font-mono text-[11px] text-gray-600 dark:text-gray-400">{result.removed_tags.map(shortRepoTag).join(', ')}</p>
          )}
          {result.failed.length > 0 && (
            <ul data-testid="host-cleanup-failed" className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
              {result.failed.map((f) => (
                <li key={f.id}><span className="font-mono">{f.id.slice(0, 12)}</span>: {f.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Last runs</h3>
      {runs.length === 0 ? (
        <p className="text-xs text-gray-500">No cleanup run yet.</p>
      ) : (
        <ul data-testid="host-cleanup-runs" className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
          {runs.slice(0, 12).map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1">
              <span className="text-gray-500">{fmtDateTime(r.started_at)}</span>
              <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 font-medium text-gray-700 dark:text-gray-300">{r.mode === 'dry_run' ? 'plan' : 'run'}</span>
              <span className="text-gray-500">{r.trigger === 'roll' ? 'after roll' : r.source}</span>
              <span className={r.status === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : r.status === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-gray-500'}>{r.status}</span>
              <span className="text-gray-600 dark:text-gray-400">{cleanupRunSummary(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function shortRepoTag(ref: string): string {
  return ref.replace(/^ghcr\.io\/orboto\//, '');
}

function HostPatchingPanel({
  server,
  onChanged,
  flash,
}: {
  server: ServerMeta;
  onChanged: () => Promise<void> | void;
  flash: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<null | 'scan' | 'patch' | 'reboot'>(null);
  const [scan, setScan] = useState<HostScanResult | null>(null);
  const [patch, setPatch] = useState<HostPatchResult | null>(null);
  const [reboot, setReboot] = useState<HostRebootResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runScan = useCallback(async () => {
    if (busy) return;
    setBusy('scan');
    setErr(null);
    try {
      const res = await api<{ server: ServerMeta; scan: HostScanResult }>(
        `/v1/admin/vps-servers/${server.id}/patch-scan`,
        { method: 'POST' },
      );
      setScan(res.scan);
      flash(
        res.scan.status === 'ok'
          ? `Scan: ${res.scan.packagesAvailable ?? 0} update(s), ${res.scan.securityAvailable ?? 0} security${res.scan.rebootRequired ? ', reboot pending' : ''}`
          : `Scan ${res.scan.status}${res.scan.skipReason ? ` (${res.scan.skipReason})` : ''}`,
      );
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `Scan failed (HTTP ${e.status}).`) : 'Scan failed.');
    } finally {
      setBusy(null);
    }
  }, [busy, server.id, flash, onChanged]);

  const runPatch = useCallback(async () => {
    if (busy) return;
    if (!window.confirm(
      'Run a full OS patch (apt-get dist-upgrade) on this host? This installs kernel updates too. IO/CPU-heavy; it does NOT reboot - a kernel update just sets "reboot pending" for the separate controlled reboot. Continue?',
    )) {
      return;
    }
    setBusy('patch');
    setErr(null);
    try {
      // OCP-503: dist_upgrade is the default so kernel meta-package updates are
      // actually installed (a plain upgrade keeps them back forever, looping the
      // counter at "0 upgraded, N still available").
      const res = await api<{ server: ServerMeta; patch: HostPatchResult }>(
        `/v1/admin/vps-servers/${server.id}/patch`,
        { method: 'POST', body: { dist_upgrade: true } },
      );
      setPatch(res.patch);
      flash(formatPatchSummary(res.patch));
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `Patch failed (HTTP ${e.status}).`) : 'Patch failed.');
    } finally {
      setBusy(null);
    }
  }, [busy, server.id, flash, onChanged]);

  const runReboot = useCallback(async () => {
    if (busy) return;
    if (!window.confirm(
      'Controlled reboot of this host?\n\nThis is DESTRUCTIVE to availability: a single-box tenant pool has no cross-box live migration, so any tenants on this host will be DOWN for the reboot. A host with tenants may only reboot inside an open maintenance window.\n\nProceed?',
    )) {
      return;
    }
    setBusy('reboot');
    setErr(null);
    const post = (extra: { override?: boolean; reason?: string }) =>
      api<{ server: ServerMeta; reboot: HostRebootResult }>(`/v1/admin/vps-servers/${server.id}/reboot`, {
        method: 'POST',
        body: { confirm: true, ...extra },
      });
    try {
      let res;
      try {
        res = await post({});
      } catch (e) {
        // OCP-807: the control-plane host refuses a reboot outside the maintenance
        // window / while other maintenance runs. An explicit override needs a reason (audited).
        const code = e instanceof ApiError && e.status === 409 ? (e.body as { error?: string; message?: string } | null)?.error : undefined;
        if (code !== 'outside_maintenance_window' && code !== 'maintenance_busy') throw e;
        const msg = (e as ApiError).body as { message?: string };
        if (!window.confirm(`${msg?.message ?? 'The reboot is refused by its guards.'}\n\nOverride the guard and reboot anyway? The reason is written to the audit log.`)) return;
        const reason = window.prompt('Reason for the override (required, audited):')?.trim();
        if (!reason) return;
        res = await post({ override: true, reason });
      }
      setReboot(res.reboot);
      flash(
        res.reboot.health === 'green'
          ? 'Reboot complete - host back reachable'
          : res.reboot.health === 'pending'
            ? 'Reboot issued - host has not returned yet (stays drained)'
            : 'Reboot could not be issued',
      );
      await onChanged();
    } catch (e) {
      // 409 maintenance_window_required / confirmation_required carry a message.
      setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `Reboot failed (HTTP ${e.status}).`) : 'Reboot failed.');
    } finally {
      setBusy(null);
    }
  }, [busy, server.id, flash, onChanged]);

  return (
    <section data-testid="host-patch-panel">
      <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Host OS patching</h2>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          A newly-registered box auto-enables unattended-upgrades (security only, no auto-reboot). Here
          you scan for pending updates, run a full patch (no reboot), or - separately, confirm + inside
          a maintenance window for a tenant host - a controlled reboot.
        </p>

        {/* Current patch status from the persisted columns. */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
          <div>
            <div className="text-gray-400">Last scanned</div>
            <div className="text-gray-700 dark:text-gray-300">{fmtPatchDate(server.last_scanned_at)}</div>
          </div>
          <div>
            <div className="text-gray-400">Last patched</div>
            <div className="text-gray-700 dark:text-gray-300">{fmtPatchDate(server.last_patched_at)}</div>
          </div>
          <div>
            <div className="text-gray-400">Updates available</div>
            <div className="text-gray-700 dark:text-gray-300">{server.packages_available ?? '-'}</div>
          </div>
          <div>
            <div className="text-gray-400">Security</div>
            <div
              className={
                (server.security_packages_available ?? 0) > 0
                  ? 'font-medium text-amber-700 dark:text-amber-400'
                  : 'text-gray-700 dark:text-gray-300'
              }
            >
              {server.security_packages_available ?? '-'}
              {/* OCP-592: how long the backlog has been pending - the alert
                  only pushes once this outlives the grace window, so the
                  duration is the operator's real signal, not the count. */}
              {(server.security_packages_available ?? 0) > 0 &&
                server.patch_alert?.security_pending_since && (
                  <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                    (pending{' '}
                    {Math.max(
                      0,
                      Math.floor(
                        (Date.now() - new Date(server.patch_alert.security_pending_since).getTime()) /
                          86_400_000,
                      ),
                    )}
                    d{server.patch_alert.alerting ? ' - alerted' : ''})
                  </span>
                )}
            </div>
          </div>
          <div>
            <div className="text-gray-400">unattended-upgrades</div>
            <div
              className={
                server.unattended_timer === 'inactive' || server.unattended_timer === 'missing'
                  ? 'font-medium text-amber-700 dark:text-amber-400'
                  : 'text-gray-700 dark:text-gray-300'
              }
              title="OCP-741 - Layer 1: the apt-daily-upgrade timer and the last run the box stamped"
            >
              {server.unattended_timer ?? '-'}
              {server.unattended_timer && (
                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                  (last run {server.unattended_last_run_at ? fmtPatchDate(server.unattended_last_run_at) : 'never'})
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2 text-sm" data-testid="host-os-info">
          <div>
            <div className="text-gray-400">OS release</div>
            <div className={server.os_drift?.behind ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}>
              {server.os_release ?? '-'}
            </div>
          </div>
          <div>
            <div className="text-gray-400">Running kernel</div>
            <div
              className={server.os_drift?.behind ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}
              title={server.os_info_at ? `read ${fmtPatchDate(server.os_info_at)} by the last scan / patch` : 'not read yet - run a scan'}
            >
              <span className="font-mono">{server.kernel_version ?? '-'}</span>
              {server.os_drift?.kernel_pending_reboot && (
                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400" data-testid="host-kernel-pending-reboot">
                  (installed {server.kernel_installed_version ?? 'newer'} - reboot pending)
                </span>
              )}
            </div>
          </div>
        </div>

        {server.os_drift?.behind && (
          <div
            data-testid="host-os-behind"
            className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
          >
            <span className="font-medium">Behind the fleet:</span> {server.os_drift.reasons.join('; ')}. The fleet runs{' '}
            {server.os_drift.fleet_os_version_id ?? '?'} with kernel {server.os_drift.fleet_kernel ?? '?'}. Patch and reboot it, or
            release-upgrade it (runbook: host-patching).
          </div>
        )}

        {server.patch_scan_error && (
          <div
            className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
            title="OCP-741 - the counts above come from the last successful refresh, not from this scan"
          >
            <span className="font-medium">Package lists stale - apt-get update failed on the last scan:</span>{' '}
            <span className="font-mono break-all">{server.patch_scan_error}</span>
          </div>
        )}

        {server.reboot_pending && (
          <div
            data-testid="host-patch-reboot-pending"
            className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300"
          >
            A pending OS update needs a reboot. Use "Controlled reboot" below (a tenant host requires an
            open maintenance window).
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="host-patch-scan"
            disabled={!server.ssh_configured || busy !== null}
            onClick={() => void runScan()}
            className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            title="Scan for pending OS + security updates (read-only)."
          >
            {busy === 'scan' ? 'Scanning…' : 'Scan'}
          </button>
          <button
            type="button"
            data-testid="host-patch-apply"
            disabled={!server.ssh_configured || busy !== null}
            onClick={() => void runPatch()}
            className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 text-xs font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 disabled:opacity-50"
            title="Full apt-get dist-upgrade, kernel updates included (no reboot)."
          >
            {busy === 'patch' ? 'Patching…' : 'Patch (no reboot)'}
          </button>
          <button
            type="button"
            data-testid="host-patch-reboot"
            disabled={!server.ssh_configured || busy !== null}
            onClick={() => void runReboot()}
            className="rounded border border-red-300 dark:border-red-700 px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 disabled:opacity-50"
            title="Controlled reboot - destructive to availability, confirm + maintenance-window gated for a tenant host."
          >
            {busy === 'reboot' ? 'Rebooting…' : 'Controlled reboot…'}
          </button>
        </div>

        {!server.ssh_configured && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No SSH credential stored - add a key or password in SSH &amp; host-agent above to enable
            patching.
          </p>
        )}

        {err && (
          <div className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">
            {err}
          </div>
        )}

        {scan && (
          <div data-testid="host-patch-scan-result" className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium">Scan {scan.status}</span>
            {scan.status === 'ok'
              ? ` - ${scan.packagesAvailable ?? 0} update(s), ${scan.securityAvailable ?? 0} security${scan.rebootRequired ? ', reboot pending' : ''}`
              : scan.skipReason
                ? ` (${scan.skipReason})`
                : ''}
          </div>
        )}

        {patch && (
          <div data-testid="host-patch-apply-result" className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <div>
              <span className="font-medium">Patch {patch.status}</span>
              {patch.status === 'ok'
                ? ` - ${patch.packagesUpdated ?? 0} package(s) upgraded, ${patch.packagesAvailable ?? 0} still available${patch.rebootRequired ? ', reboot pending' : ''}`
                : patch.skipReason
                  ? ` (${patch.skipReason})`
                  : ''}
            </div>
            {patch.status === 'ok' && (patch.keptBack ?? 0) > 0 && (
              // OCP-503: honest residual - dist-upgrade could not install these
              // (phased rollout / apt-mark hold), so say so rather than imply a
              // clean patch.
              <div data-testid="host-patch-kept-back" className="text-amber-700 dark:text-amber-400">
                {patch.keptBack} package(s) kept back (held/phased) - not a loop, these are deferred by apt.
              </div>
            )}
            {patch.agent && (
              // OCP-504: the patch also converges the box's report agent (reuse
              // OCP-469). `skipped` = no agent installed (use Install agent);
              // `failed` is non-fatal (agent drift is silent, not tenant-blocking).
              <div
                data-testid="host-patch-agent"
                className={patch.agent === 'failed' ? 'text-amber-700 dark:text-amber-400' : undefined}
              >
                <span className="text-gray-400">Host agent:</span> {AGENT_LABELS[patch.agent]}
                {patch.agentVersion && <span className="ml-1 font-mono text-gray-500">v{patch.agentVersion}</span>}
              </div>
            )}
            {patch.proxyAfterDockerUpgrade && patch.proxyAfterDockerUpgrade !== 'not_needed' && (
              // OCP-807: after a docker engine upgrade the proxy is restarted and its
              // logs are watched for the stale-socket error (OCP-806).
              <div
                data-testid="host-patch-proxy-docker"
                className={patch.proxyAfterDockerUpgrade === 'failed' ? 'text-red-700 dark:text-red-400' : undefined}
              >
                <span className="text-gray-400">coolify-proxy:</span>{' '}
                {patch.proxyAfterDockerUpgrade === 'restarted' ? 'restarted after the docker upgrade, socket ok' : 'socket error after the restart'}
                {patch.proxyAfterDockerUpgradeDetail && <span className="ml-1 text-gray-500">({patch.proxyAfterDockerUpgradeDetail})</span>}
              </div>
            )}
            {patch.outputTail && (
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-all text-[11px] text-gray-500">{patch.outputTail}</pre>
            )}
          </div>
        )}

        {reboot && (
          <div data-testid="host-patch-reboot-result" className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <div className="font-medium">
              {reboot.health === 'green' ? (
                <span className="text-green-700 dark:text-green-400">Reboot complete - host back reachable</span>
              ) : reboot.health === 'pending' ? (
                <span className="text-amber-700 dark:text-amber-400">Reboot issued - host has not returned yet (stays drained)</span>
              ) : (
                <span className="text-red-700 dark:text-red-400">Reboot could not be issued</span>
              )}
            </div>
            <div>
              tenants on host: {reboot.tenant_count}
              {reboot.containers_running != null ? ` · containers running: ${reboot.containers_running}` : ''}
            </div>
            <div className={reboot.single_box_downtime ? 'text-amber-700 dark:text-amber-400' : undefined}>{reboot.note}</div>
          </div>
        )}
      </div>
    </section>
  );
}

// OCP-420 - per-mount disk-badge display config.
type MountState = 'primary' | 'include' | 'hidden';

interface MountRow {
  mount: string;
  fs_type: string | null;
  total_gb: number;
  used_gb: number;
  used_pct: number | null;
  inode_pct: number | null;
  state: MountState;
  is_system: boolean;
  is_badge: boolean;
}

const MOUNT_STATE_LABELS: Record<MountState, { label: string; desc: string }> = {
  primary: { label: 'Primary', desc: 'The servers-overview disk badge shows this mount.' },
  include: { label: 'Include', desc: 'Normal data mount, eligible for the worst-mount badge.' },
  hidden: { label: 'Hidden', desc: 'Excluded from the badge (still shown here + still alerted on).' },
};

/**
 * OCP-420 - disk-badge mount display config. Lists every mount from the
 * latest host beacon with a three-state selector (primary / include /
 * hidden), prefilled by the AUTO heuristic so the operator confirms rather
 * than types (wizard-first, OCP-416). Hidden is display-only: the mount
 * stays visible here and overload alerting still evaluates it. Persists the
 * full map via PATCH (audited as vps_server.mount_config.changed).
 */
/**
 * OCP-464 - live NIC throughput + FairFlat month-to-date network volume for
 * one server. NIC (current + peak Mbit/s, tx utilization vs the uplink) is the
 * live network-pressure signal that also gates the capacity model; the volume
 * bar shows the month-to-date FairFlat consumption as a % of the effective
 * allowance, with a per-server monthly-allowance override editor. Self-loading
 * (GET /:id/network), mirrors MountDisplayPanel.
 */
interface NetworkData {
  nic: {
    snapshot_at: string | null;
    uplink_mbits: number;
    rx_mbits: number | null;
    tx_mbits: number | null;
    rx_mbits_peak: number | null;
    tx_mbits_peak: number | null;
    tx_pct_of_uplink: number | null;
  };
  volume: {
    cycle_month: string | null;
    rx_bytes: number | null;
    tx_bytes: number | null;
    total_bytes: number | null;
    included_gb: number;
    included_gb_override: number | null;
    used_pct: number | null;
    alerted_at: string | null;
  };
}

// ---- OCP-622: GPU / model-host panel --------------------------------

interface GpuRow {
  index: number;
  name: string;
  vram_used_mb: number;
  vram_total_mb: number;
  vram_pct: number | null;
  util_pct?: number | null;
  temp_c?: number | null;
}

interface GpuData {
  snapshot_at: string | null;
  gpus: GpuRow[];
  backend: {
    evaluated_at: string;
    dimension: { pct: number | null; detail: string; unavailable: string | null } | null;
    vllm: {
      model_name: string | null;
      running: number;
      waiting: number;
      kv_cache_pct: number | null;
      awake: boolean | null;
      preemptions_total: number | null;
      latency_p95_ms: number | null;
      latency_samples: number;
      url: string;
    } | null;
    vllm_unavailable: string | null;
  } | null;
}

function pctBarClass(pct: number | null): string {
  if (pct == null) return 'bg-gray-300';
  if (pct >= 92) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

/**
 * OCP-622 - per-GPU VRAM / utilisation / temperature from the latest beacon
 * (any host whose agent found nvidia-smi) and, on the ai-backend role, the
 * vLLM numbers the last capacity check recorded (running / waiting / KV cache
 * / p95 / awake) with the backend dimension verdict.
 */
function GpuPanel({ serverId, serverRole, agentOnly }: { serverId: string; serverRole: string; agentOnly: boolean }) {
  const [data, setData] = useState<GpuData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api<Partial<GpuData>>(`/v1/admin/vps-servers/${serverId}/gpu`);
        if (alive) setData({ snapshot_at: res.snapshot_at ?? null, gpus: Array.isArray(res.gpus) ? res.gpus : [], backend: res.backend ?? null });
      } catch (e) {
        if (alive) setErr(e instanceof ApiError ? `Could not load GPU data (HTTP ${e.status}).` : 'Could not load GPU data.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [serverId]);

  // Only a model host, or a box that actually reports GPUs, gets the card.
  if (!err && data && data.gpus.length === 0 && serverRole !== 'ai-backend') return null;

  return (
    <section className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" data-testid="gpu-panel">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          GPUs {serverRole === 'ai-backend' ? '+ model host' : ''}
        </h2>
        {data?.snapshot_at && (
          <span className="text-xs text-gray-500">beacon {fmtDateTime(data.snapshot_at)}</span>
        )}
      </div>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      {data && data.gpus.length === 0 && !err && (
        <p className="mt-2 text-xs text-gray-500">
          {data.snapshot_at
            ? 'The beacon carries no GPU samples - nvidia-smi is not on this box, or the agent predates the GPU probe (reinstall it).'
            : agentOnly
              ? 'No beacon yet - run the install command on the box; the first beacon lands within 60 s.'
              : 'No beacon yet.'}
        </p>
      )}
      {data && data.gpus.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-1 pr-3">GPU</th>
                <th className="py-1 pr-3">VRAM</th>
                <th className="py-1 pr-3 w-40"></th>
                <th className="py-1 pr-3">Util</th>
                <th className="py-1 pr-3">Temp</th>
              </tr>
            </thead>
            <tbody>
              {data.gpus.map((g) => (
                <tr key={g.index} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1 pr-3 font-mono">#{g.index} {g.name}</td>
                  <td className="py-1 pr-3 tabular-nums">
                    {(g.vram_used_mb / 1024).toFixed(1)} / {(g.vram_total_mb / 1024).toFixed(1)} GB
                    {g.vram_pct != null ? ` (${g.vram_pct}%)` : ''}
                  </td>
                  <td className="py-1 pr-3">
                    <div className="h-2 w-40 rounded bg-gray-100 dark:bg-gray-800">
                      <div className={`h-2 rounded ${pctBarClass(g.vram_pct)}`} style={{ width: `${Math.min(100, g.vram_pct ?? 0)}%` }} />
                    </div>
                  </td>
                  <td className="py-1 pr-3 tabular-nums">{g.util_pct != null ? `${Math.round(g.util_pct)}%` : '-'}</td>
                  <td className="py-1 pr-3 tabular-nums">{g.temp_c != null ? `${Math.round(g.temp_c)} °C` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {serverRole === 'ai-backend' && data && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-3 text-xs" data-testid="vllm-block">
          {data.backend ? (
            <>
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Backend dimension</span>{' '}
                {data.backend.dimension?.pct != null ? (
                  <span className={`rounded px-1.5 py-0.5 font-medium ${data.backend.dimension.pct >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {data.backend.dimension.pct}%
                  </span>
                ) : (
                  <span className="text-gray-500">not measurable{data.backend.dimension?.unavailable ? ` - ${data.backend.dimension.unavailable}` : ''}</span>
                )}{' '}
                <span className="text-gray-500">as of {fmtDateTime(data.backend.evaluated_at)}</span>
              </p>
              {data.backend.dimension?.detail && <p className="mt-1 text-gray-500">{data.backend.dimension.detail}</p>}
              {data.backend.vllm ? (
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                  <dt className="text-gray-500">Model</dt>
                  <dd className="font-mono">{data.backend.vllm.model_name ?? '-'}</dd>
                  <dt className="text-gray-500">Engine</dt>
                  <dd>{data.backend.vllm.awake == null ? '-' : data.backend.vllm.awake ? 'awake' : 'ASLEEP'}</dd>
                  <dt className="text-gray-500">Running / waiting</dt>
                  <dd className="tabular-nums">{data.backend.vllm.running} / {data.backend.vllm.waiting}</dd>
                  <dt className="text-gray-500">KV cache</dt>
                  <dd className="tabular-nums">{data.backend.vllm.kv_cache_pct != null ? `${data.backend.vllm.kv_cache_pct}%` : '-'}</dd>
                  <dt className="text-gray-500">p95 (since last check)</dt>
                  <dd className="tabular-nums">{data.backend.vllm.latency_p95_ms != null ? `${data.backend.vllm.latency_p95_ms} ms over ${data.backend.vllm.latency_samples} req` : `no requests (${data.backend.vllm.latency_samples})`}</dd>
                  <dt className="text-gray-500">Preemptions (lifetime)</dt>
                  <dd className="tabular-nums">{data.backend.vllm.preemptions_total ?? '-'}</dd>
                </dl>
              ) : (
                <p className="mt-1 text-amber-700">vLLM metrics unavailable: {data.backend.vllm_unavailable ?? 'unknown'}</p>
              )}
            </>
          ) : (
            <p className="text-gray-500">
              No capacity reading names this host yet - the check runs every 5 minutes (<code>ai-gateway.capacity-check</code>); the vLLM scrape URL is <code>ai.backend_metrics_url</code>.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function fmtMbit(v: number | null): string {
  if (v == null) return '-';
  return v >= 1000 ? `${(v / 1000).toFixed(1)} Gbit/s` : `${v.toFixed(v < 10 ? 1 : 0)} Mbit/s`;
}

function fmtVolBytes(bytes: number | null): string {
  if (bytes == null) return '-';
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(0)} MB`;
  return `${bytes} B`;
}

function fmtAllowance(gb: number): string {
  return gb >= 1000 ? `${(gb / 1000).toFixed(gb % 1000 === 0 ? 0 : 1)} TB` : `${gb} GB`;
}

function NetworkPanel({
  serverId,
  flash,
  setError,
}: {
  serverId: string;
  flash: (msg: string) => void;
  setError: (msg: string | null) => void;
}) {
  const [data, setData] = useState<NetworkData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setData(await api<NetworkData>(`/v1/admin/vps-servers/${serverId}/network`));
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load network (HTTP ${e.status}).` : 'Could not load network.');
    }
  }, [serverId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSaveAllowance = useCallback(
    async (value: number | null) => {
      try {
        await api(`/v1/admin/vps-servers/${serverId}`, {
          method: 'PATCH',
          body: { network_volume_included_gb: value },
        });
        flash(value == null ? 'volume allowance override cleared' : `volume allowance override -> ${value} GB`);
        await load();
      } catch (e) {
        const msg = e instanceof ApiError ? `HTTP ${e.status}` : 'unknown';
        setError(`Could not update network_volume_included_gb (${msg}).`);
      }
    },
    [serverId, flash, load, setError],
  );

  const txPct = data?.nic?.tx_pct_of_uplink ?? null;
  const volPct = data?.volume?.used_pct ?? null;
  const volTone = volPct != null && volPct >= 80 ? 'bg-red-500' : volPct != null && volPct >= 60 ? 'bg-amber-500' : 'bg-brand-500';
  const nicTone = txPct != null && txPct >= 80 ? 'bg-red-500' : txPct != null && txPct >= 60 ? 'bg-amber-500' : 'bg-brand-500';
  const ready = data?.nic != null && data?.volume != null;

  return (
    <div
      data-testid="network-panel"
      className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Network</h3>
        {data?.volume?.alerted_at && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
            Volume alert fired
          </span>
        )}
      </div>

      {err && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{err}</p>}
      {!data && !err && <p className="mt-2 text-xs text-gray-500">Loading…</p>}

      {ready && data && (
        <div className="mt-3 space-y-4">
          {/* NIC throughput bar (tx vs uplink). */}
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">NIC throughput (tx)</span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300" data-testid="nic-tx-pct">
                {txPct == null ? '-' : `${txPct}%`}
                <span className="ml-1 text-gray-400">
                  · {fmtMbit(data.nic.tx_mbits)} tx
                  {data.nic.tx_mbits_peak != null ? ` · peak ${fmtMbit(data.nic.tx_mbits_peak)}` : ''}
                  {` · of ${data.nic.uplink_mbits} Mbit uplink`}
                </span>
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
              <div className={`h-full ${nicTone}`} style={{ width: `${Math.min(100, txPct ?? 0)}%` }} />
            </div>
            <div className="mt-1 text-[11px] text-gray-400">
              rx {fmtMbit(data.nic.rx_mbits)}
              {data.nic.rx_mbits_peak != null ? ` · peak ${fmtMbit(data.nic.rx_mbits_peak)}` : ''}
              {data.nic.snapshot_at ? ` · ${formatRelative(data.nic.snapshot_at)}` : ' · no beacon yet'}
            </div>
          </div>

          {/* FairFlat month-to-date volume bar (% of the effective allowance). */}
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">
                Volume this month{data.volume.cycle_month ? ` (since ${data.volume.cycle_month})` : ''}
              </span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300" data-testid="volume-pct">
                {volPct == null ? '-' : `${volPct}%`}
                <span className="ml-1 text-gray-400">
                  · {fmtVolBytes(data.volume.total_bytes)} / {fmtAllowance(data.volume.included_gb)}
                </span>
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
              <div className={`h-full ${volTone}`} style={{ width: `${Math.min(100, volPct ?? 0)}%` }} />
            </div>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
              <span>Monthly allowance:</span>
              <AllowanceEditor value={data.volume.included_gb_override} onSave={onSaveAllowance} />
              {data.volume.included_gb_override == null && (
                <span className="text-gray-400">(fleet default {fmtAllowance(data.volume.included_gb)})</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** OCP-464 - per-server monthly network-volume allowance override editor (GB). */
function AllowanceEditor({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (v: number | null) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(value == null ? '' : String(value));
  return editing ? (
    <span className="inline-flex items-center gap-1 text-xs">
      <input
        type="number"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        min={1}
        max={10000000}
        placeholder="default"
        aria-label="network_volume_included_gb"
        className="w-24 rounded border border-gray-300 dark:border-gray-700 px-1 py-0.5 text-xs"
      />
      <span className="text-gray-400">GB</span>
      <button
        type="button"
        onClick={async () => {
          const trimmed = next.trim();
          await onSave(trimmed === '' ? null : Number(trimmed));
          setEditing(false);
        }}
        className="rounded bg-brand-500 px-2 py-0.5 text-xs text-white"
      >
        save
      </button>
      <button
        type="button"
        onClick={() => {
          setNext(value == null ? '' : String(value));
          setEditing(false);
        }}
        className="rounded border border-gray-300 dark:border-gray-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400"
      >
        cancel
      </button>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-xs text-gray-700 dark:text-gray-300 hover:text-brand-600"
      title="Edit per-server monthly volume allowance"
    >
      {value == null ? 'fleet default (edit)' : `${value} GB (edit)`}
    </button>
  );
}

function MountDisplayPanel({
  serverId,
  flash,
}: {
  serverId: string;
  flash: (msg: string) => void;
}) {
  const [mounts, setMounts] = useState<MountRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, MountState>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await api<{ mounts: MountRow[] }>(
        `/v1/admin/vps-servers/${serverId}/mount-config`,
      );
      setMounts(res.mounts);
      // Prefill the draft from the effective (heuristic-or-explicit) state.
      setDraft(Object.fromEntries(res.mounts.map((m) => [m.mount, m.state])));
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load mounts (HTTP ${e.status}).` : 'Could not load mounts.');
    }
  }, [serverId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setMountState = useCallback((mount: string, next: MountState) => {
    setDraft((d) => {
      const updated = { ...d, [mount]: next };
      // At most one primary - demote any previous primary to include.
      if (next === 'primary') {
        for (const k of Object.keys(updated)) {
          if (k !== mount && updated[k] === 'primary') updated[k] = 'include';
        }
      }
      return updated;
    });
  }, []);

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setErr(null);
    try {
      await api(`/v1/admin/vps-servers/${serverId}/mount-config`, {
        method: 'PATCH',
        body: { mount_config: draft },
      });
      flash('Disk mount display saved');
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? `Save failed (HTTP ${e.status}).` : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [saving, serverId, draft, flash, load]);

  if (err && !mounts) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Disk mount display</h2>
        <p className="text-sm text-red-600 dark:text-red-400">{err}</p>
      </section>
    );
  }
  if (!mounts) return null;
  if (mounts.length === 0) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Disk mount display</h2>
        <p className="text-sm text-gray-500">No mounts reported by the host agent yet.</p>
      </section>
    );
  }

  const dirty = mounts.some((m) => draft[m.mount] !== m.state);

  return (
    <section data-testid="mount-display-panel">
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Disk mount display</h2>
        <span className="text-xs text-gray-500">
          Controls the servers-overview disk badge. Does not affect alerting.
        </span>
      </div>
      <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Mount</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2 text-right">Used</th>
              <th className="px-3 py-2">Display</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {mounts.map((m) => {
              const state = draft[m.mount] ?? m.state;
              const dim = state === 'hidden';
              return (
                <tr key={m.mount} className={dim ? 'opacity-60' : ''}>
                  <td className="px-3 py-2">
                    <span className="font-mono">
                      {m.is_badge ? '● ' : ''}
                      {m.mount}
                    </span>
                    {m.fs_type && <span className="ml-2 text-xs text-gray-500">{m.fs_type}</span>}
                    {m.is_system && (
                      <span className="ml-2 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-500">
                        system
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {m.total_gb} GB
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {m.used_gb} GB{m.used_pct != null ? ` · ${m.used_pct}%` : ''}
                    {m.inode_pct != null && m.inode_pct >= 80 ? ` · inodes ${Math.round(m.inode_pct)}%` : ''}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={`Display state for ${m.mount}`}
                      value={state}
                      onChange={(e) => setMountState(m.mount, e.target.value as MountState)}
                      className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-xs"
                    >
                      {(['primary', 'include', 'hidden'] as const).map((v) => (
                        <option key={v} value={v} title={MOUNT_STATE_LABELS[v].desc}>
                          {MOUNT_STATE_LABELS[v].label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {err && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{err}</p>}
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => void save()}
          className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save display config'}
        </button>
        {dirty && <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        <span className="text-[11px] text-gray-500">
          Prefilled from the auto heuristic - system/tiny mounts are hidden by default.
        </span>
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: 'active' | 'draining' | 'decommissioned' }) {
  const styles =
    status === 'active'
      ? 'bg-green-100 text-green-800'
      : status === 'draining'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${styles}`}>{status}</span>;
}

/**
 * OCP-436: effective-capacity snapshot. Shows the static RAM-derived baseline
 * next to the dynamically-computed effective cap, the binding dimension, and
 * (from effective_capacity_inputs) the exact probe values + three model
 * dimensions that produced it - so the operator can see WHY the cap moved.
 * Also hosts the uplink-rating + operator-hard-cap editors.
 *
 * OCP-551: resolution goes through the shared `resolveEffectiveCapacity()`
 * so this panel and the top "Tenant slots" capacity card can never disagree.
 */
function EffectiveCapacityPanel({
  server,
  onSaveUplink,
  onSaveOverride,
}: {
  server: ServerMeta;
  onSaveUplink: (v: number) => void | Promise<void>;
  onSaveOverride: (v: number | null) => void | Promise<void>;
}) {
  const inputs = server.effective_capacity_inputs ?? null;
  const resolved = resolveEffectiveCapacity(server);
  const staticCap = resolved.static;
  const override = resolved.override;
  const effective = resolved.effective;
  const dims = inputs?.dimensions;
  return (
    <div
      data-testid="effective-capacity-panel"
      className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Effective capacity
        </h3>
        {inputs && (
          <span
            className={`rounded px-2 py-0.5 text-[10px] font-medium ${
              inputs.hot
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {BINDING_LABEL[inputs.binding] ?? inputs.binding}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Static baseline</div>
          <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {staticCap}
          </div>
          <div className="text-[10px] text-gray-500">RAM-derived floor</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Effective cap</div>
          <div
            data-testid="effective-cap-value"
            className="text-lg font-semibold tabular-nums text-brand-600 dark:text-brand-400"
          >
            {effective}
          </div>
          <div className="text-[10px] text-gray-500">
            {server.effective_max_tenants == null ? 'not yet computed' : 'gates new placements'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Operator hard cap</div>
          <div className="text-sm tabular-nums text-gray-900 dark:text-gray-100">
            <OverrideEditor value={override} onSave={onSaveOverride} />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Uplink</div>
          <div className="text-sm tabular-nums text-gray-900 dark:text-gray-100">
            <UplinkEditor value={server.uplink_mbits ?? 1000} onSave={onSaveUplink} />
          </div>
        </div>
      </div>

      {dims && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-400">
          <span>
            model = min(cpu <strong className="tabular-nums">{dims.cpu ?? 'n/a'}</strong>, ram{' '}
            <strong className="tabular-nums">{dims.ram}</strong>, uplink{' '}
            <strong className="tabular-nums">{dims.uplink ?? 'n/a'}</strong>)
          </span>
          {inputs && (
            <>
              <span>
                CPU p95 <strong className="tabular-nums">{inputs.probes.hostCpuP95 == null ? 'n/a' : `${Math.round(inputs.probes.hostCpuP95)}%`}</strong>
              </span>
              <span>
                RAM p95 <strong className="tabular-nums">{inputs.probes.hostRamP95 == null ? 'n/a' : `${Math.round(inputs.probes.hostRamP95)}%`}</strong>
              </span>
              {/* OCP-464 - live NIC pressure p95 as a % of the uplink (the
                  additive headroom gate). Red when it crossed the hot bar. */}
              <span className={inputs.probes.nicHot ? 'text-red-600 dark:text-red-400' : undefined}>
                NIC p95{' '}
                <strong className="tabular-nums">
                  {inputs.probes.nicUtilP95Pct == null ? 'n/a' : `${Math.round(inputs.probes.nicUtilP95Pct)}%`}
                </strong>
                {inputs.probes.nicTxP95Mbits != null && (
                  <span className="text-gray-400"> ({Math.round(inputs.probes.nicTxP95Mbits)} Mbit tx)</span>
                )}
              </span>
              <span>
                CCU sum <strong className="tabular-nums">{inputs.probes.ccuSum}</strong>
              </span>
              <span>
                samples <strong className="tabular-nums">{inputs.probes.sampleCount}</strong>
              </span>
            </>
          )}
        </div>
      )}

      {inputs && (
        <div
          data-testid="effective-capacity-constants"
          className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-500"
        >
          <span>
            RAM/tenant{' '}
            <strong className="tabular-nums">
              {inputs.probes.tenantsWithTelemetry > 0
                ? `${inputs.probes.avgRamMbPerTenant} MB (measured)`
                : `${inputs.constants?.idleRamMbPerTenant ?? inputs.probes.avgRamMbPerTenant} MB (idle)`}
            </strong>
          </span>
          {inputs.constants && (
            <>
              <span>
                CCU/core <strong className="tabular-nums">{inputs.constants.ccuPerCore}</strong>
              </span>
              <span>
                CCU/Mbit <strong className="tabular-nums">{inputs.constants.ccuPerMbit}</strong>
              </span>
              <span>
                expected CCU/tenant{' '}
                <strong className="tabular-nums">{inputs.constants.expectedCcuPerTenant}</strong>
              </span>
            </>
          )}
        </div>
      )}

      {inputs ? (
        <p className="mt-2 text-[11px] leading-snug text-gray-500">{inputs.reason}</p>
      ) : (
        <p className="mt-2 text-[11px] leading-snug text-gray-500">
          No effective value computed yet - consumers fall back to the static baseline
          (clamped by the operator hard cap). The capacity job runs every 5 min and on each
          host beacon.
        </p>
      )}
    </div>
  );
}

function UplinkEditor({ value, onSave }: { value: number; onSave: (v: number) => void | Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(String(value));
  return editing ? (
    <span className="inline-flex items-center gap-1 text-xs">
      <input
        type="number"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        min={1}
        max={100000}
        aria-label="uplink_mbits"
        className="w-20 rounded border border-gray-300 dark:border-gray-700 px-1 py-0.5 text-xs"
      />
      <button
        type="button"
        onClick={async () => {
          await onSave(Number(next));
          setEditing(false);
        }}
        className="rounded bg-brand-500 px-2 py-0.5 text-xs text-white"
      >
        save
      </button>
      <button
        type="button"
        onClick={() => {
          setNext(String(value));
          setEditing(false);
        }}
        className="rounded border border-gray-300 dark:border-gray-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400"
      >
        cancel
      </button>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-xs text-gray-700 dark:text-gray-300 hover:text-brand-600"
      title="Edit uplink_mbits"
    >
      {value} Mbit (edit)
    </button>
  );
}

function OverrideEditor({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (v: number | null) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(value == null ? '' : String(value));
  return editing ? (
    <span className="inline-flex items-center gap-1 text-xs">
      <input
        type="number"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        min={1}
        max={2000}
        placeholder="none"
        aria-label="max_tenants_override"
        className="w-16 rounded border border-gray-300 dark:border-gray-700 px-1 py-0.5 text-xs"
      />
      <button
        type="button"
        onClick={async () => {
          const trimmed = next.trim();
          await onSave(trimmed === '' ? null : Number(trimmed));
          setEditing(false);
        }}
        className="rounded bg-brand-500 px-2 py-0.5 text-xs text-white"
      >
        save
      </button>
      <button
        type="button"
        onClick={() => {
          setNext(value == null ? '' : String(value));
          setEditing(false);
        }}
        className="rounded border border-gray-300 dark:border-gray-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400"
      >
        cancel
      </button>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-xs text-gray-700 dark:text-gray-300 hover:text-brand-600"
      title="Edit operator hard cap"
    >
      {value == null ? 'none (edit)' : `${value} (edit)`}
    </button>
  );
}

function CapacityCard({
  title,
  used,
  total,
  unit,
  caption,
}: {
  title: string;
  used: number;
  total: number;
  unit: string;
  /**
   * OCP-551: optional context line under the "% in use" row - e.g. the
   * static baseline + why the effective cap diverges from it. Only render
   * when the caller has something worth saying (no static/effective
   * mismatch on a card = no caption).
   */
  caption?: string;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const tone = pct >= 85 ? 'red' : pct >= 60 ? 'yellow' : 'green';
  const barColor =
    tone === 'red' ? 'bg-red-500' : tone === 'yellow' ? 'bg-yellow-500' : 'bg-green-500';
  const testId = `capacity-card-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div
      data-testid={testId}
      className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">{used}</span>
        <span className="text-sm text-gray-500">/ {total} {unit}</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded bg-gray-100 dark:bg-gray-800">
        <div className={`h-1.5 rounded ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[10px] text-gray-500">{pct}% in use</div>
      {caption && <div className="mt-1 text-[10px] text-gray-500" title={caption}>{caption}</div>}
    </div>
  );
}

// OCP-551: label reads "static cap" (not the bare `max_tenants` column
// name) because the OCP-436 model gates real placement on the EFFECTIVE
// cap shown in the "Tenant slots" card + EffectiveCapacityPanel below -
// editing this value alone does not change what the scheduler will do.
function MaxTenantsEditor({ value, onSave }: { value: number; onSave: (v: number) => void | Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(String(value));
  return editing ? (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="text-gray-500">static cap</span>
      <input
        type="number"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        min={1}
        max={2000}
        className="w-16 rounded border border-gray-300 dark:border-gray-700 px-1 py-0.5 text-xs"
      />
      <button
        type="button"
        onClick={async () => {
          await onSave(Number(next));
          setEditing(false);
        }}
        className="rounded bg-brand-500 px-2 py-0.5 text-xs text-white"
      >
        save
      </button>
      <button
        type="button"
        onClick={() => {
          setNext(String(value));
          setEditing(false);
        }}
        className="rounded border border-gray-300 dark:border-gray-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-400"
      >
        cancel
      </button>
    </span>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-xs text-gray-600 dark:text-gray-400 hover:text-brand-600"
      title="Edit the static hardware-class baseline (vps_servers.max_tenants). The scheduler gates placement on the effective cap shown above, not this value."
    >
      static cap = {value} (edit)
    </button>
  );
}

function SparklineTable({ rows }: { rows: HistoryPoint[] }) {
  const maxRam = Math.max(1, ...rows.map((r) => r.ram_used_mb_sum));
  const maxCpu = Math.max(1, ...rows.map((r) => r.cpu_pct_avg));
  return (
    <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
      <table className="w-full text-xs">
        <thead className="bg-gray-100 dark:bg-gray-800 text-left uppercase tracking-wide text-gray-600 dark:text-gray-400">
          <tr>
            <th className="px-3 py-1.5">Time</th>
            <th className="px-3 py-1.5 text-right">Tenants</th>
            <th className="px-3 py-1.5 text-right">RAM MB</th>
            <th className="px-3 py-1.5">RAM bar</th>
            <th className="px-3 py-1.5 text-right">CPU %</th>
            <th className="px-3 py-1.5">CPU bar</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
          {rows.map((r) => {
            const ramPct = Math.round((r.ram_used_mb_sum / maxRam) * 100);
            const cpuPct = Math.round((r.cpu_pct_avg / maxCpu) * 100);
            return (
              <tr key={r.bucket}>
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                  {fmtDateTime(r.bucket)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.tenant_count}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.ram_used_mb_sum}</td>
                <td className="px-3 py-1.5">
                  <div className="h-1 w-full max-w-[120px] rounded bg-gray-100 dark:bg-gray-800">
                    <div className="h-1 rounded bg-blue-500" style={{ width: `${ramPct}%` }} />
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {Math.round(r.cpu_pct_avg)}
                </td>
                <td className="px-3 py-1.5">
                  <div className="h-1 w-full max-w-[120px] rounded bg-gray-100 dark:bg-gray-800">
                    <div className="h-1 rounded bg-emerald-500" style={{ width: `${cpuPct}%` }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
